import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import adminService from '../services/adminService';
import MapCanvas from '../components/admin/MapCanvas';
import TileSelector from '../components/admin/TileSelector';
import GalleryModal from '../components/admin/GalleryModal';
import MapTestModal from '../components/admin/MapTestModal';
import TilesetConfigurator from '../components/admin/TilesetConfigurator';
import RpgWorldManager from '../components/admin/RpgWorldManager';
import { normalizeTilesetConfig, parseTilesetConfig } from '../data/tilesets';
import {
  Package,
  Users,
  MapIcon,
  Activity,
  Database,
  ArrowRight,
  Plus,
  Edit2,
  Trash2,
  ChevronLeft,
  Grid,
  Save,
  BarChart3,
  TrendingUp,
  ChevronRight,
  Settings,
  Home,
  Image,
  Cpu,
  Gamepad2,
  Leaf,
  RefreshCcw
} from 'lucide-react';

const ITEM_TYPES = [
  { value: 0, label: 'Arma' },
  { value: 1, label: 'Kit Médico' },
  { value: 2, label: 'Cargador Munición' },
  { value: 3, label: 'Objeto' },
  { value: 4, label: 'Objeto Especial' },
  { value: 5, label: 'Metal/Mineral' },
  { value: 6, label: 'Madera' },
  { value: 7, label: 'Mina/Explosivo' },
  { value: 8, label: 'Motor' },
  { value: 9, label: 'Radar' },
  { value: 10, label: 'Mochila' },
  { value: 11, label: 'Guerra electronica' },
  { value: 12, label: 'Bateria' },
  { value: 13, label: 'Verdura / Cultivo' },
  { value: 14, label: 'Semilla' },
  { value: 15, label: 'Edificio / Estructura' },
];

const CITY_TYPE_LABELS = {
  0: 'Campamento',
  1: 'Pueblo',
  2: 'Ciudad',
  3: 'Capital'
};

function HelpTooltip({ text }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <span className="relative inline-flex ml-2">
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        className="w-5 h-5 rounded-full bg-slate-700 text-slate-400 hover:bg-game-primary hover:text-white transition-colors text-xs font-bold flex items-center justify-center"
      >?</button>
      {visible && (
        <div className="absolute left-7 top-0 z-50 w-60 p-3 bg-slate-800 border border-slate-600 rounded-xl text-xs text-slate-300 shadow-2xl">
          {text}
          <button onClick={() => setVisible(false)} className="absolute top-1 right-2 text-slate-500 hover:text-white">X</button>
        </div>
      )}
    </span>
  );
}

function buildEnemySuggestionFromLevel(level) {
  const normalizedLevel = Math.max(1, Number(level) || 1);
  const totalHp = Math.round(70 + normalizedLevel * 14 + Math.pow(normalizedLevel, 1.08) * 4);
  const v_head = Math.max(8, Math.round(totalHp * 0.12));
  const v_torso = Math.max(16, Math.round(totalHp * 0.34));
  const v_arm_l = Math.max(8, Math.round(totalHp * 0.145));
  const v_arm_r = Math.max(8, Math.round(totalHp * 0.145));
  const used = v_head + v_torso + v_arm_l + v_arm_r;
  const remaining = Math.max(12, totalHp - used);
  const v_leg_l = Math.round(remaining / 2);
  const v_leg_r = remaining - v_leg_l;
  const experience_reward = Math.round(normalizedLevel * 18 + totalHp * 0.35);
  const money_reward = Math.round(normalizedLevel * 12 + totalHp * 0.18);

  return {
    v_head,
    v_torso,
    v_arm_l,
    v_arm_r,
    v_leg_l,
    v_leg_r,
    experience_reward,
    money_reward
  };
}

function parseEnemyMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return typeof metadata === 'object' ? metadata : {};
}

function parseJsonSetting(value, fallback) {
  if (!value) return fallback;
  if (typeof value !== 'string') return value && typeof value === 'object' ? value : fallback;
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
}

export default function Admin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rpgWorldManagerRef = React.useRef(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showEnemyModal, setShowEnemyModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [showSectorModal, setShowSectorModal] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryTarget, setGalleryTarget] = useState(null); // 'item' | 'enemy' | 'city' | 'rpg-tileset' | 'rpg-city-config' | 'rpg-npc-template' | 'rpg-player-appearance'
  const [editingItem, setEditingItem] = useState(null);
  const [editingEnemy, setEditingEnemy] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [editingSector, setEditingSector] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    weight: 0,
    power: 0,
    item_type: 3,
    image: 'default.png',
    level_required: 1,
    value: 0,
    metadata: {}
  });

  const [enemyForm, setEnemyForm] = useState({
    name: '',
    level: 1,
    v_head: 20,
    v_torso: 40,
    v_arm_l: 20,
    v_arm_r: 20,
    v_leg_l: 30,
    v_leg_r: 30,
    image: 'enemy1.png',
    experience_reward: 10,
    money_reward: 5,
    metadata: {}
  });

  const [cityForm, setCityForm] = useState({
    name: '',
    coordinate: null,
    id_sector: null,
    image: 'city1.png',
    clan: 0,
    city_type: 0
  });

  const [sectorForm, setSectorForm] = useState({
    name: '',
    width: 20,
    height: 15,
    description: '',
    image: '',
    sector_type: 0,
    temperature: 20,
    climate: 'templado',
    tileset_id: 'overworld_v1'
  });
  const [missionForm, setMissionForm] = useState({
    name: '',
    description: '',
    level_required: 1,
    reward_money: 0,
    reward_experience: 0,
    reward_item: 0,
    is_active: 1
  });
  const [missionEventForm, setMissionEventForm] = useState({
    id_mission: '',
    event_text: '',
    coordinate: 0,
    id_sector: '',
    reward_money_give: 0,
    reward_money_take: 0,
    reward_item_give: 0,
    reward_item_take: 0,
    reward_quantity: 1,
    image: '',
    metadata: {
      event_enemy_id: 0,
      event_teleport_sector: 0,
      event_teleport_coordinate: 0,
      player_level_required: 0
    },
    is_active: 1
  });
  const [selectedMissionEventId, setSelectedMissionEventId] = useState(null);
  const [selectedMissionForEdit, setSelectedMissionForEdit] = useState(null);
  const [hoveredMapEventId, setHoveredMapEventId] = useState(null);
  const [showMissionEventEditModal, setShowMissionEventEditModal] = useState(false);
  const [editingMissionEvent, setEditingMissionEvent] = useState(null);
  const [missionEventEditForm, setMissionEventEditForm] = useState(null);
  const [selectedAdminClanId, setSelectedAdminClanId] = useState(null);
  const [selectedLeaderUserId, setSelectedLeaderUserId] = useState('');
  const [selectedAdminUserId, setSelectedAdminUserId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    country: '',
    is_blocked: false,
    is_confirmed: false,
    is_admin: false,
    onboarding_step: 'welcome',
    selected_clan_id: '',
    intro_seen: false,
    has_completed_onboarding: false
  });

  const { data: adminStats, isLoading } = useQuery('adminStats', adminService.getStats);
  const { data: itemsData, isLoading: isLoadingItems } = useQuery('adminItems', adminService.getAllItems, {
    enabled: activeTab === 'items' || activeTab === 'maps' || activeTab === 'missions' || activeTab === 'rpg'
  });
  const { data: enemiesData, isLoading: isLoadingEnemies } = useQuery('adminEnemies', adminService.getAllEnemies, {
    enabled: activeTab === 'enemies' || activeTab === 'maps' || activeTab === 'missions'
  });
  const { data: citiesData, isLoading: isLoadingCities } = useQuery('adminCities', adminService.getAllCities, {
    enabled: activeTab === 'cities' || activeTab === 'maps' || activeTab === 'rpg'
  });
  const { data: clansData = [], isLoading: isLoadingClans } = useQuery('adminClans', adminService.getAllClans, {
    enabled: activeTab === 'cities' || activeTab === 'maps' || activeTab === 'rpg'
  });
  const { data: adminClansData = [], isLoading: isLoadingAdminClans } = useQuery('adminClansMaster', adminService.getAdminClans, {
    enabled: activeTab === 'clans' || activeTab === 'users'
  });
  const { data: adminUsersData, isLoading: isLoadingAdminUsers } = useQuery('adminUsers', adminService.getAllUsers, {
    enabled: activeTab === 'users'
  });
  const { data: adminClanMembersData, isLoading: isLoadingAdminClanMembers } = useQuery(
    ['adminClanMembers', selectedAdminClanId],
    () => adminService.getAdminClanMembers(selectedAdminClanId),
    { enabled: activeTab === 'clans' && !!selectedAdminClanId }
  );
  const { data: adminClanActivityData, isLoading: isLoadingAdminClanActivity } = useQuery(
    ['adminClanActivity', selectedAdminClanId],
    () => adminService.getAdminClanActivity(selectedAdminClanId),
    { enabled: activeTab === 'clans' && !!selectedAdminClanId }
  );
  const { data: sectorsData, isLoading: isLoadingSectors } = useQuery('adminSectors', adminService.getAllSectors, {
    enabled: activeTab === 'maps' || activeTab === 'missions' || activeTab === 'tilesets'
  });
  const { data: globalSettings, isLoading: isLoadingSettings } = useQuery('adminSettings', adminService.getSettings, {
    enabled: activeTab === 'settings' || activeTab === 'tilesets'
  });
  const { data: rpgTilesetsData = [], isLoading: isLoadingRpgTilesets } = useQuery('adminRpgTilesets', adminService.getRpgTilesets, {
    enabled: activeTab === 'rpg'
  });
  const { data: rpgCitiesData = [], isLoading: isLoadingRpgCities } = useQuery('adminRpgCities', adminService.getRpgCities, {
    enabled: activeTab === 'rpg'
  });
  const { data: rpgNpcsData = { templates: [], instances: [] }, isLoading: isLoadingRpgNpcs } = useQuery('adminRpgNpcs', adminService.getRpgNpcs, {
    enabled: activeTab === 'rpg'
  });
  const { data: rpgCropTransformationsData = [] } = useQuery('adminRpgCropTransformations', adminService.getRpgCropTransformations, {
    enabled: activeTab === 'rpg'
  });
  const { data: rpgPlayerAppearanceData = null } = useQuery('adminRpgPlayerAppearance', adminService.getRpgPlayerAppearance, {
    enabled: activeTab === 'rpg'
  });
  const { data: missionsDataRaw = [], isLoading: isLoadingMissions } = useQuery('adminMissions', adminService.getAllMissions, {
    enabled: activeTab === 'missions' || activeTab === 'maps'
  });
  const { data: missionEventsDataRaw = [], isLoading: isLoadingMissionEvents } = useQuery('adminMissionEvents', adminService.getAllMissionEvents, {
    enabled: activeTab === 'missions' || activeTab === 'maps'
  });
  const missionsData = Array.isArray(missionsDataRaw)
      ? missionsDataRaw
      : (missionsDataRaw?.missions || missionsDataRaw?.data || []);
  const missionEventsData = Array.isArray(missionEventsDataRaw)
      ? missionEventsDataRaw
      : (missionEventsDataRaw?.missionEvents || missionEventsDataRaw?.events || missionEventsDataRaw?.data || []);
  const adminCities = React.useMemo(() => {
    if (Array.isArray(citiesData)) return citiesData;
    if (Array.isArray(citiesData?.cities)) return citiesData.cities;
    if (Array.isArray(citiesData?.data)) return citiesData.data;
    return [];
  }, [citiesData]);
  const missionEventsForSelectedMission = selectedMissionForEdit
    ? missionEventsData.filter(ev => Number(ev.id_mission) === Number(selectedMissionForEdit.id))
    : [];
  const safeClansData = Array.isArray(clansData) ? clansData : [];
  const clansById = React.useMemo(() => {
    const map = new Map();
    safeClansData.forEach((c) => map.set(Number(c.id), c));
    return map;
  }, [safeClansData]);

  const [selectedSectorId, setSelectedSectorId] = useState(null);
  const { data: sectorDetail, isLoading: isLoadingDetail } = useQuery(
    ['adminSectorDetail', selectedSectorId],
    () => adminService.getSectorDetails(selectedSectorId),
    { enabled: !!selectedSectorId && activeTab === 'maps' }
  );

  const [settingsForm, setSettingsForm] = useState({});
  useEffect(() => {
    if (globalSettings) {
      setSettingsForm(globalSettings);
    }
  }, [globalSettings]);
  const tilesetOverworldConfig = React.useMemo(() => {
    return normalizeTilesetConfig(parseTilesetConfig(globalSettings?.tileset_overworld_v1));
  }, [globalSettings]);
  const rpgSpecialItems = React.useMemo(() => {
    const source = Array.isArray(itemsData) ? itemsData : [];
    return source.filter((item) => [4, 13, 14, 15].includes(Number(item.item_type)));
  }, [itemsData]);
  const rpgConfiguredCities = React.useMemo(() => {
    const source = Array.isArray(rpgCitiesData) ? rpgCitiesData : [];
    return source.filter((entry) => !!entry?.config);
  }, [rpgCitiesData]);
  const rpgNpcTemplates = React.useMemo(() => Array.isArray(rpgNpcsData?.templates) ? rpgNpcsData.templates : [], [rpgNpcsData]);
  const rpgNpcInstances = React.useMemo(() => Array.isArray(rpgNpcsData?.instances) ? rpgNpcsData.instances : [], [rpgNpcsData]);
  const rpgCropTransformations = React.useMemo(() => Array.isArray(rpgCropTransformationsData) ? rpgCropTransformationsData : [], [rpgCropTransformationsData]);
  const rpgPlayerAppearance = React.useMemo(() => rpgPlayerAppearanceData?.appearance || null, [rpgPlayerAppearanceData]);
  useEffect(() => {
    if (!Array.isArray(adminClansData) || !adminClansData.length) {
      setSelectedAdminClanId(null);
      return;
    }
    if (!selectedAdminClanId) {
      setSelectedAdminClanId(adminClansData[0].id);
    }
  }, [adminClansData, selectedAdminClanId]);
  useEffect(() => {
    const leaderId = adminClanMembersData?.clan?.leader_id;
    setSelectedLeaderUserId(leaderId ? String(leaderId) : '');
  }, [adminClanMembersData?.clan?.leader_id, selectedAdminClanId]);
  const adminUsers = Array.isArray(adminUsersData?.users) ? adminUsersData.users : [];
  const adminUsersSummary = adminUsersData?.summary || { total: 0, blocked: 0, admins: 0, withBot: 0, withoutBot: 0 };
  const adminClanOptions = Array.isArray(adminClansData) ? adminClansData : [];
  const selectedAdminUser = React.useMemo(
    () => adminUsers.find((user) => Number(user.id) === Number(selectedAdminUserId)) || null,
    [adminUsers, selectedAdminUserId]
  );
  const filteredAdminUsers = React.useMemo(() => {
    const search = userSearch.trim().toLowerCase();
    return adminUsers.filter((user) => {
      const matchesSearch = !search ||
        user.username?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        String(user.id).includes(search) ||
        (user.bot?.name || '').toLowerCase().includes(search);

      const matchesFilter = (() => {
        switch (userStatusFilter) {
          case 'blocked':
            return user.is_blocked;
          case 'admins':
            return user.is_admin;
          case 'with-bot':
            return Number(user.bot_count || 0) > 0;
          case 'without-bot':
            return Number(user.bot_count || 0) === 0;
          default:
            return true;
        }
      })();

      return matchesSearch && matchesFilter;
    });
  }, [adminUsers, userSearch, userStatusFilter]);

  useEffect(() => {
    if (!adminUsers.length) {
      setSelectedAdminUserId(null);
      return;
    }
    if (!selectedAdminUserId || !adminUsers.some((user) => Number(user.id) === Number(selectedAdminUserId))) {
      setSelectedAdminUserId(adminUsers[0].id);
    }
  }, [adminUsers, selectedAdminUserId]);

  useEffect(() => {
    if (!selectedAdminUser) return;
    setUserForm({
      username: selectedAdminUser.username || '',
      email: selectedAdminUser.email || '',
      country: selectedAdminUser.country || '',
      is_blocked: !!selectedAdminUser.is_blocked,
      is_confirmed: !!selectedAdminUser.is_confirmed,
      is_admin: !!selectedAdminUser.is_admin,
      onboarding_step: selectedAdminUser.onboarding_step || 'welcome',
      selected_clan_id: selectedAdminUser.selected_clan_id ? String(selectedAdminUser.selected_clan_id) : '',
      intro_seen: !!selectedAdminUser.intro_seen,
      has_completed_onboarding: !!selectedAdminUser.has_completed_onboarding
    });
  }, [selectedAdminUserId, selectedAdminUser]);

  const [localTiles, setLocalTiles] = useState([]);
  const [activeLayer, setActiveLayer] = useState(0);
  const [selectedDrawingTile, setSelectedDrawingTile] = useState({ type: 'terrain', id: 0 });

  // Nuevos estados para el editor de mapas Fase 1
  const [editorMode, setEditorMode] = useState('terrain'); // 'terrain', 'cities', 'enemies', 'objects', 'portals'
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedEnemyId, setSelectedEnemyId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isBuried, setIsBuried] = useState(false);
  const [selectedPortalTargetId, setSelectedPortalTargetId] = useState(null);
  const [destinationCoordinate, setDestinationCoordinate] = useState(null);
  const [localPortals, setLocalPortals] = useState([]);
  const [showMapTestModal, setShowMapTestModal] = useState(false);
  const [entityModal, setEntityModal] = useState(null); // { type: 'city'|'enemy'|'object'|'portal'|'event', coordinate, data }
  const [entityModalForm, setEntityModalForm] = useState({});

  useEffect(() => {
    if (sectorDetail?.tiles) {
      setLocalTiles(sectorDetail.tiles);
    }
    // Cargar portales del sector si existen
    if (sectorDetail?.portals) {
      setLocalPortals(sectorDetail.portals);
    }
  }, [sectorDetail]);

  // Preparar instancias de enemigos para el mapa actual.
  // Si el backend devuelve enemy_instances vivos, priorizamos ese estado.
  const liveEnemyInstances = (sectorDetail?.enemyInstances || []).map((enemy) => {
    const enemyMeta = (enemiesData || []).find((entry) => Number(entry.id) === Number(enemy.id_enemy));
    return {
      ...enemy,
      name: enemyMeta?.name || enemy.name,
      level: enemyMeta?.level || enemy.level
    };
  });

  const enemyInstances = liveEnemyInstances.length > 0 ? liveEnemyInstances : localTiles
    .filter(t => t.layer === 2 && t.id_enemy_map && t.id_enemy_map > 0)
    .map(t => {
      const enemy = (enemiesData || []).find(e => e.id === t.id_enemy_map);
      return { ...t, name: enemy?.name, level: enemy?.level };
    });

  const objectInstances = localTiles
    .filter(t => t.layer === 1 && t.id_object && t.id_object > 0)
    .map(t => {
      const item = (itemsData || []).find(i => i.id === t.id_object);
      return { ...t, name: item?.name, item_type: item?.item_type, image: item?.image, source: 'map' };
    });
  const groundObjectInstances = (sectorDetail?.groundItems || []).map((item) => ({
    ...item,
    source: 'ground'
  }));
  const renderedObjectInstances = [...objectInstances, ...groundObjectInstances];
  const objectModalItems = entityModal?.type === 'object'
    ? (Array.isArray(entityModal.data?.items) ? entityModal.data.items : [entityModal.data].filter(Boolean))
    : [];
  const objectModalSelectedId = Number(entityModalForm.selectedObjectId || entityModalForm.id_object || objectModalItems[0]?.id || objectModalItems[0]?.id_object || 0);
  const objectModalSelectedItem = objectModalItems.find((item) => Number(item.id || item.id_object) === objectModalSelectedId) || objectModalItems[0] || null;
  const eventInstances = localTiles
    .filter(t => t.layer === 2 && t.id_event && t.id_event > 0)
    .map(t => {
      const ev = (missionEventsData || []).find(me => me.id === t.id_event);
      const mission = (missionsData || []).find(m => Number(m.id) === Number(ev?.id_mission));
      return {
        ...t,
        id_event: t.id_event,
        id_mission: ev?.id_mission,
        event_text: ev?.event_text,
        mission_name: ev?.mission_name || mission?.name || ''
      };
    });
  const placedMissionEventMap = React.useMemo(() => {
    const map = new Map();
    (missionEventsData || []).forEach((eventRow) => {
      const eventId = Number(eventRow.id);
      const eventSectorId = Number(eventRow.id_sector);
      const hasGlobalPlacement =
        Number.isInteger(eventSectorId) &&
        eventSectorId > 0 &&
        eventRow.coordinate !== null &&
        eventRow.coordinate !== undefined;

      if (hasGlobalPlacement && eventSectorId !== Number(selectedSectorId)) {
        map.set(eventId, {
          id_event: eventId,
          coordinate: Number(eventRow.coordinate),
          id_sector: eventSectorId,
          event_text: eventRow.event_text,
          mission_name: eventRow.mission_name || ''
        });
      }
    });

    eventInstances.forEach((instance) => {
      map.set(Number(instance.id_event), {
        ...instance,
        id_sector: Number(selectedSectorId)
      });
    });
    return map;
  }, [eventInstances, missionEventsData, selectedSectorId]);
  const sectorCities = adminCities.filter((c) => c.id_sector === selectedSectorId);

  const createMutation = useMutation(adminService.createItem, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminItems');
      setShowItemModal(false);
      resetForm();
    }
  });

  const updateMutation = useMutation((data) => adminService.updateItem(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminItems');
      setShowItemModal(false);
      resetForm();
    }
  });

  const createEnemyMutation = useMutation(adminService.createEnemy, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminEnemies');
      setShowEnemyModal(false);
      resetEnemyForm();
    }
  });
  const updateEnemyMutation = useMutation((data) => adminService.updateEnemy(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminEnemies');
      setShowEnemyModal(false);
      resetEnemyForm();
    }
  });

  const createCityMutation = useMutation(adminService.createCity, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminCities');
      setShowCityModal(false);
      resetCityForm();
    },
    onError: (err) => {
      alert('Error al crear ciudad: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  });

  const updateCityMutation = useMutation((data) => adminService.updateCity(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminCities');
      setShowCityModal(false);
      resetCityForm();
    },
    onError: (err) => {
      alert('Error al actualizar ciudad: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  });
  const createSectorMutation = useMutation(adminService.createSector, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminSectors');
      resetSectorForm();
      setShowSectorModal(false);
      alert('Sector creado con éxito');
    },
    onError: (err) => {
      alert('Error al crear sector: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  });
  const updateSectorMutation = useMutation((data) => adminService.updateSector(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminSectors');
      if (selectedSectorId) {
        queryClient.invalidateQueries(['adminSectorDetail', selectedSectorId]);
      }
      resetSectorForm();
      setShowSectorModal(false);
      alert('Sector actualizado con éxito');
    },
    onError: (err) => {
      alert('Error al actualizar sector: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    }
  });
  const createMissionMutation = useMutation(adminService.createMission, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminMissions');
      setMissionForm({
        name: '',
        description: '',
        level_required: 1,
        reward_money: 0,
        reward_experience: 0,
        reward_item: 0,
        is_active: 1
      });
      alert('Misión creada');
    }
  });
  const createMissionEventMutation = useMutation(adminService.createMissionEvent, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminMissionEvents');
      setMissionEventForm({
        id_mission: '',
        event_text: '',
        coordinate: 0,
        id_sector: '',
        reward_money_give: 0,
        reward_money_take: 0,
        reward_item_give: 0,
        reward_item_take: 0,
        reward_quantity: 1,
        image: '',
        metadata: {
          event_enemy_id: 0,
          event_teleport_sector: 0,
          event_teleport_coordinate: 0,
          player_level_required: 0
        },
        is_active: 1
      });
      alert('Evento de misión creado');
    }
  });
  const updateMissionEventMutation = useMutation((data) => adminService.updateMissionEvent(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminMissionEvents');
      setShowMissionEventEditModal(false);
      setEditingMissionEvent(null);
      setMissionEventEditForm(null);
      alert('Evento de misión actualizado');
    }
  });
  const updateGridMutation = useMutation(
    (data) => adminService.updateSectorGrid(data.id, { tiles: data.tiles, portals: data.portals }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['adminSectorDetail', selectedSectorId]);
        queryClient.invalidateQueries('adminCities');
        queryClient.invalidateQueries('adminMissionEvents');
        alert('Mapa guardado con éxito');
      },
      onError: (err) => {
        alert('Error al guardar el mapa: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
      }
    }
  );
  const generateWorldEntitiesMutation = useMutation(
    (data) => adminService.generateSectorWorldEntities(data.id, data.payload),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['adminSectorDetail', selectedSectorId]);
        const summary = result?.summary || {};
        const debugSuffix = Number(summary.enemiesPlaced || 0) + Number(summary.objectsPlaced || 0) + Number(summary.minesPlaced || 0) === 0
          ? ` | libres: ${summary.walkableTiles || 0}, alcanzables: ${summary.reachableTiles || 0}, candidatos: ${summary.candidateTiles || 0}`
          : '';
        alert(
          `Entidades generadas: ${summary.enemiesPlaced || 0} enemigos, ` +
          `${summary.objectsPlaced || 0} objetos, ${summary.minesPlaced || 0} minas${debugSuffix}`
        );
      },
      onError: (err) => {
        alert('Error al generar entidades: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
      }
    }
  );

  const updateSettingsMutation = useMutation(adminService.updateSettings, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminSettings');
      alert('Configuraciones actualizadas con éxito');
    }
  });

  const createRpgTilesetMutation = useMutation(adminService.createRpgTileset, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgTilesets');
    }
  });
  const updateRpgTilesetMutation = useMutation((data) => adminService.updateRpgTileset(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgTilesets');
    }
  });
  const deleteRpgTilesetMutation = useMutation(adminService.deleteRpgTileset, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgTilesets');
    }
  });

  const saveRpgCityConfigMutation = useMutation((data) => adminService.saveRpgCityConfig(data.cityId, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCities');
    }
  });
  const saveRpgCityMapMutation = useMutation((data) => adminService.saveRpgCityMap(data.cityId, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCities');
      queryClient.invalidateQueries('adminRpgCityMap');
    }
  });
  const deleteRpgCityConfigMutation = useMutation(adminService.deleteRpgCityConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCities');
    }
  });
  const createRpgNpcTemplateMutation = useMutation(adminService.createRpgNpcTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
    }
  });
  const createRpgCropTransformationMutation = useMutation(adminService.createRpgCropTransformation, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCropTransformations');
    }
  });
  const updateRpgCropTransformationMutation = useMutation((data) => adminService.updateRpgCropTransformation(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCropTransformations');
    }
  });
  const deleteRpgCropTransformationMutation = useMutation(adminService.deleteRpgCropTransformation, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgCropTransformations');
    }
  });
  const updateRpgNpcTemplateMutation = useMutation((data) => adminService.updateRpgNpcTemplate(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
    }
  });
  const deleteRpgNpcTemplateMutation = useMutation(adminService.deleteRpgNpcTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
    }
  });
  const createRpgCityNpcMutation = useMutation(adminService.createRpgCityNpc, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
      queryClient.invalidateQueries('adminRpgCityMap');
    }
  });
  const updateRpgCityNpcMutation = useMutation((data) => adminService.updateRpgCityNpc(data.id, data.form), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
      queryClient.invalidateQueries('adminRpgCityMap');
    }
  });
  const deleteRpgCityNpcMutation = useMutation(adminService.deleteRpgCityNpc, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgNpcs');
      queryClient.invalidateQueries('adminRpgCityMap');
    }
  });
  const saveRpgPlayerAppearanceMutation = useMutation(adminService.saveRpgPlayerAppearance, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminRpgPlayerAppearance');
    }
  });

  const cleanupMutation = useMutation(adminService.triggerCleanup, {
    onSuccess: (data) => {
      alert(data.message);
    }
  });

  // MUTACIONES DE ELIMINACIÓN
  const deleteItemMutation = useMutation(adminService.deleteItem, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminItems');
      alert('Objeto eliminado con éxito');
    }
  });

  const deleteGroundItemMutation = useMutation(adminService.deleteGroundItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSectorDetail', selectedSectorId]);
      alert('Objeto del suelo eliminado con éxito');
    }
  });

  const deleteEnemyMutation = useMutation(adminService.deleteEnemy, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminEnemies');
      alert('Enemigo eliminado con éxito');
    }
  });

  const deleteCityMutation = useMutation(adminService.deleteCity, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminCities');
      alert('Ciudad eliminada con éxito');
    }
  });

  const deleteSectorMutation = useMutation(adminService.deleteSector, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminSectors');
      alert('Sector eliminado con éxito');
    }
  });
  const deleteMissionMutation = useMutation(adminService.deleteMission, {
    onSuccess: () => queryClient.invalidateQueries('adminMissions')
  });
  const deleteMissionEventMutation = useMutation(adminService.deleteMissionEvent, {
    onSuccess: () => queryClient.invalidateQueries('adminMissionEvents')
  });
  const setClanLeaderMutation = useMutation(
    ({ clanId, leaderUserId }) => adminService.setAdminClanLeader(clanId, leaderUserId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminClansMaster');
        queryClient.invalidateQueries(['adminClanMembers', selectedAdminClanId]);
        queryClient.invalidateQueries(['adminClanActivity', selectedAdminClanId]);
        alert('Jefe de clan actualizado');
      },
      onError: (error) => {
        alert(error?.response?.data?.error || 'Error al actualizar jefe de clan');
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ id, form }) => adminService.updateUser(id, form),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        queryClient.invalidateQueries('adminStats');
        alert('Usuario actualizado correctamente');
      },
      onError: (error) => {
        alert(error?.response?.data?.error || 'Error al actualizar usuario');
      }
    }
  );

  const blockUserMutation = useMutation(adminService.blockUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('adminStats');
      alert('Usuario bloqueado');
    },
    onError: (error) => {
      alert(error?.response?.data?.error || 'Error al bloquear usuario');
    }
  });

  const unblockUserMutation = useMutation(adminService.unblockUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('adminStats');
      alert('Usuario desbloqueado');
    },
    onError: (error) => {
      alert(error?.response?.data?.error || 'Error al desbloquear usuario');
    }
  });

  const resetUserMutation = useMutation(adminService.resetUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('adminStats');
      alert('Usuario reiniciado');
    },
    onError: (error) => {
      alert(error?.response?.data?.error || 'Error al reiniciar usuario');
    }
  });

  const deleteUserMutation = useMutation(adminService.deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('adminStats');
      alert('Usuario eliminado definitivamente');
      setSelectedAdminUserId((current) => {
        const remaining = filteredAdminUsers.filter((user) => Number(user.id) !== Number(current));
        return remaining[0]?.id || adminUsers.find((user) => Number(user.id) !== Number(current))?.id || null;
      });
    },
    onError: (error) => {
      alert(error?.response?.data?.error || 'Error al eliminar usuario');
    }
  });

  const handleDelete = (id, type, name) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar "${name}"? Esta acción no se puede deshacer.`)) {
      switch (type) {
        case 'item': deleteItemMutation.mutate(id); break;
        case 'enemy': deleteEnemyMutation.mutate(id); break;
        case 'city': deleteCityMutation.mutate(id); break;
        case 'sector': deleteSectorMutation.mutate(id); break;
        case 'mission': deleteMissionMutation.mutate(id); break;
        case 'mission-event': deleteMissionEventMutation.mutate(id); break;
        default: break;
      }
    }
  };

  const handleTilePaint = (coordinate, layer, tile) => {
    setLocalTiles(prev => {
      // Filtrar tiles en la misma coordenada Y capa (permite múltiples capas)
      const filtered = prev.filter(t => !(t.coordinate === coordinate && t.layer === layer));
      const metadata = { ...(tile.metadata || {}) };
      if (tile.type === 'terrain' && tile.variantCell && tile.variantMode === 'manual') {
        metadata.terrain_variant = {
          x: Number(tile.variantCell.x || 0),
          y: Number(tile.variantCell.y || 0),
          index: Number(tile.variantIndex ?? 0)
        };
      } else {
        delete metadata.terrain_variant;
      }
      return [
        ...filtered,
        {
          coordinate,
          layer,
          terrain_type: tile.type === 'terrain' ? tile.id : (layer === 0 ? 0 : -1),
          id_object: tile.type === 'object' ? tile.id : 0,
          id_enemy_map: tile.type === 'enemy' ? tile.id : 0,
          is_buried: tile.is_buried || false,
          metadata
        }
      ];
    });
  };

  const handleTileErase = (coordinate, layer) => {
    setLocalTiles(prev => prev.filter(t => !(t.coordinate === coordinate && t.layer === layer)));
  };

  // ==================== HANDLERS PARA ENTIDADES (FASE 1) ====================

  // Colocar ciudad en el mapa
  const handlePlaceCity = async (coordinate) => {
    if (!selectedCityId) {
      alert('Primero selecciona una ciudad del panel lateral');
      return;
    }

    // Verificar que la coordenada no esté ocupada por otra ciudad del mismo sector
    const existingCity = adminCities.find((c) => c.coordinate === coordinate && c.id_sector === selectedSectorId && c.id !== selectedCityId);
    if (existingCity) {
      alert('Ya hay una ciudad en esta coordenada');
      return;
    }

    const cityToUpdate = adminCities.find((c) => c.id === selectedCityId);
    if (!cityToUpdate) return;
    const alreadyPlaced =
      cityToUpdate.id_sector !== null &&
      cityToUpdate.id_sector !== undefined &&
      cityToUpdate.coordinate !== null &&
      cityToUpdate.coordinate !== undefined;
    if (alreadyPlaced) {
      alert(`La ciudad "${cityToUpdate.name}" ya está colocada y no se puede volver a colocar.`);
      setSelectedCityId(null);
      return;
    }

    try {
      // Llamar a la API para actualizar la coordenada de la ciudad
      await adminService.updateCity(selectedCityId, {
        ...cityToUpdate,
        coordinate,
        id_sector: selectedSectorId
      });
      queryClient.invalidateQueries('adminCities');
      alert(`Ciudad "${cityToUpdate.name}" colocada en coordenada ${coordinate}`);
    } catch (err) {
      alert('Error al colocar la ciudad: ' + (err.response?.data?.error || err.message));
    }

    setSelectedCityId(null);
  };

  // Colocar enemigo en el mapa
  const handlePlaceEnemy = (coordinate) => {
    if (!selectedEnemyId) {
      alert('Primero selecciona un enemigo del panel lateral');
      return;
    }

    // Verificar que no haya otro enemigo en la misma coordenada (capa 2)
    const existingEnemy = localTiles.find(t => t.coordinate === coordinate && t.layer === 2 && t.id_enemy_map > 0);
    if (existingEnemy) {
      alert('Ya hay un enemigo en esta coordenada');
      return;
    }

    // Añadir el enemigo a localTiles en capa 2
    setLocalTiles(prev => {
      const filtered = prev.filter(t => !(t.coordinate === coordinate && t.layer === 2));
      return [
        ...filtered,
        {
          coordinate,
          layer: 2,
          terrain_type: -1,
          id_object: 0,
          id_enemy_map: selectedEnemyId,
          is_buried: false,
          metadata: {}
        }
      ];
    });

    const enemyName = enemiesData?.find(e => e.id === selectedEnemyId)?.name || 'Enemigo';
    alert(`"${enemyName}" colocado en coordenada ${coordinate}. Pulsa Guardar Mapa para persistir.`);
    setSelectedEnemyId(null);
  };

  // Colocar objeto en el mapa
  const handlePlaceObject = (coordinate) => {
    if (!selectedItemId) {
      alert('Primero selecciona un objeto del panel lateral');
      return;
    }

    // El objeto comparte fila con la capa 1 para no destruir el terreno base.
    setLocalTiles(prev => {
      const existingLayerTile = prev.find(t =>
        Number(t.coordinate) === Number(coordinate) && Number(t.layer) === 1
      );

      if (existingLayerTile) {
        return prev.map(t => {
          if (Number(t.coordinate) !== Number(coordinate) || Number(t.layer) !== 1) return t;
          return {
            ...t,
            id_object: selectedItemId,
            id_enemy_map: 0,
            is_buried: isBuried,
            metadata: { ...(t.metadata || {}), buried: isBuried }
          };
        });
      }

      return [
        ...prev,
        {
          coordinate,
          layer: 1,
          terrain_type: -1,
          id_object: selectedItemId,
          id_enemy_map: 0,
          is_buried: isBuried,
          metadata: { buried: isBuried }
        }
      ];
    });

    const itemName = itemsData?.find(i => i.id === selectedItemId)?.name || 'Objeto';
    alert(`"${itemName}" colocado en coordenada ${coordinate}${isBuried ? ' (enterrado)' : ''}. Pulsa Guardar Mapa para persistir.`);
    setSelectedItemId(null);
    setIsBuried(false);
  };

  // Colocar portal en el mapa
  const handlePlacePortal = () => {
    if (!selectedPortalTargetId || destinationCoordinate === null || destinationCoordinate === undefined) {
      alert('Primero selecciona un sector destino y una coordenada de destino');
      return;
    }

    // Encontrar una coordenada libre para colocar el portal
    // En una implementación real, el admin haría clic en el mapa para seleccionar la coordenada origen
    const newPortal = {
      id_sector_origen: selectedSectorId,
      coordenada_origen: 0, // Se asignaría al hacer clic en el mapa
      id_sector_destino: selectedPortalTargetId,
      coordenada_destino: destinationCoordinate
    };

    // En una implementación real, llamaríamos a la API
    setLocalPortals(prev => [...prev, newPortal]);
    alert(`Portal creado hacia sector ${selectedPortalTargetId}, coordenada ${destinationCoordinate}`);

    // Resetear selección
    setSelectedPortalTargetId(null);
    setDestinationCoordinate(null);
  };

  // Eliminar portal
  const handleDeletePortal = (portalId) => {
    setLocalPortals(prev => prev.filter(p => p.id !== portalId));
    alert('Portal eliminado');
  };

  const openEntityModal = (type, coordinate, data) => {
    setEntityModal({ type, coordinate, data });
    if (type === 'city') {
      setEntityModalForm({
        id: data.id,
        name: data.name || '',
        city_type: data.city_type || 0,
        clan: data.clan || 0,
        coordinate: data.coordinate
      });
    } else if (type === 'enemy') {
      setEntityModalForm({
        coordinate: data.coordinate,
        id_enemy_map: data.id_enemy_map || 0
      });
    } else if (type === 'object') {
      const firstObject = Array.isArray(data?.items) ? data.items[0] : data;
      setEntityModalForm({
        coordinate,
        id_object: firstObject?.id_object || firstObject?.id || 0,
        is_buried: !!(firstObject?.is_buried || firstObject?.metadata?.buried),
        selectedObjectId: firstObject?.id || firstObject?.id_object || 0
      });
    } else if (type === 'portal') {
      setEntityModalForm({
        coordinate: data.coordenada_origen,
        id_sector_destino: data.id_sector_destino || 0,
        coordenada_destino: data.coordenada_destino ?? 0
      });
    } else if (type === 'event') {
      setEntityModalForm({
        coordinate: data.coordinate,
        id_event: data.id_event || 0
      });
    }
  };

  const closeEntityModal = () => {
    setEntityModal(null);
    setEntityModalForm({});
  };

  const handleEntityModalSave = async () => {
    if (!entityModal) return;

    if (entityModal.type === 'city') {
      const original = entityModal.data;
      try {
        await updateCityMutation.mutateAsync({
          id: original.id,
          form: {
            ...original,
            id_sector: selectedSectorId,
            coordinate: Number(entityModalForm.coordinate),
            city_type: Number(entityModalForm.city_type),
            clan: Number(entityModalForm.clan),
            name: entityModalForm.name || original.name
          }
        });
        alert('Ciudad actualizada.');
        closeEntityModal();
      } catch (err) {
        alert('Error al actualizar ciudad: ' + (err.response?.data?.error || err.message));
      }
      return;
    }

    if (entityModal.type === 'enemy') {
      setLocalTiles(prev => prev.map(t => {
        if (t.coordinate === entityModal.coordinate && t.layer === 2) {
          return {
            ...t,
            id_enemy_map: Number(entityModalForm.id_enemy_map) || 0,
            metadata: t.metadata || {}
          };
        }
        return t;
      }));
      alert('Enemigo actualizado en el mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'object') {
      const items = Array.isArray(entityModal.data?.items) ? entityModal.data.items : [entityModal.data].filter(Boolean);
      const selectedId = Number(entityModalForm.selectedObjectId || entityModalForm.id_object || items[0]?.id || items[0]?.id_object || 0);
      const selectedItem = items.find((item) => Number(item.id || item.id_object) === selectedId) || items[0];
      if (!selectedItem) {
        alert('No se pudo identificar el objeto seleccionado.');
        return;
      }

      if (selectedItem.source === 'ground') {
        alert('Los objetos del suelo se eliminan desde el botón Eliminar.');
        return;
      }

      setLocalTiles(prev => prev.map(t => {
        if (t.coordinate === entityModal.coordinate && t.layer === 1 && Number(t.id_object) > 0) {
          const buried = !!entityModalForm.is_buried;
          const targetTileObjectId = Number(t.id_object);
          const selectedObjectId = Number(selectedItem.id_object || selectedItem.id || 0);
          if (targetTileObjectId !== selectedObjectId) return t;
          return {
            ...t,
            id_object: Number(entityModalForm.id_object) || 0,
            is_buried: buried,
            metadata: { ...(t.metadata || {}), buried }
          };
        }
        return t;
      }));
      alert('Objeto actualizado en el mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'portal') {
      const oldOrigin = entityModal.data.coordenada_origen;
      const oldDestSector = Number(entityModal.data.id_sector_destino);
      const oldDestCoord = Number(entityModal.data.coordenada_destino);
      const newDestSector = Number(entityModalForm.id_sector_destino) || oldDestSector;
      const newDestCoord = Number(entityModalForm.coordenada_destino) || 0;
      const currentSector = Number(selectedSectorId);

      setLocalPortals(prev => {
        // 1) Actualizar portal principal (origen clicado)
        let next = prev.map(p => {
          if (p.coordenada_origen === oldOrigin) {
            return {
              ...p,
              id_sector_destino: newDestSector,
              coordenada_destino: newDestCoord
            };
          }
          return p;
        });

        // 2) Limpiar reverso antiguo si existía en este mismo sector
        next = next.filter(p => !(
          Number(p.id_sector_destino) === currentSector &&
          Number(p.coordenada_origen) === oldDestCoord &&
          Number(p.coordenada_destino) === oldOrigin
        ));

        // 3) Si el nuevo destino es el mismo sector, crear/actualizar reverso nuevo
        if (newDestSector === currentSector && newDestCoord !== oldOrigin) {
          const reverseExists = next.some(p =>
            Number(p.coordenada_origen) === newDestCoord &&
            Number(p.id_sector_destino) === currentSector &&
            Number(p.coordenada_destino) === oldOrigin
          );
          if (!reverseExists) {
            next.push({
              id_sector_origen: currentSector,
              coordenada_origen: newDestCoord,
              id_sector_destino: currentSector,
              coordenada_destino: oldOrigin
            });
          }
        }

        return next;
      });
      alert('Portal actualizado en el mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'event') {
      setLocalTiles(prev => prev.map(t => {
        if (t.coordinate === entityModal.coordinate && t.layer === 2) {
          return {
            ...t,
            id_event: Number(entityModalForm.id_event) || 0,
            id_enemy_map: 0
          };
        }
        return t;
      }));
      alert('Evento actualizado en el mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
    }
  };

  const handleEntityModalDelete = async () => {
    if (!entityModal) return;

    if (entityModal.type === 'city') {
      try {
        await deleteCityMutation.mutateAsync(entityModal.data.id);
        closeEntityModal();
      } catch (err) {
        alert('Error al eliminar ciudad: ' + (err.response?.data?.error || err.message));
      }
      return;
    }

    if (entityModal.type === 'enemy') {
      setLocalTiles(prev => prev.filter(t => !(t.coordinate === entityModal.coordinate && t.layer === 2)));
      alert('Enemigo eliminado del mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'object') {
      const items = Array.isArray(entityModal.data?.items) ? entityModal.data.items : [entityModal.data].filter(Boolean);
      const selectedId = Number(entityModalForm.selectedObjectId || entityModalForm.id_object || items[0]?.id || items[0]?.id_object || 0);
      const selectedItem = items.find((item) => Number(item.id || item.id_object) === selectedId) || items[0];

      if (!selectedItem) {
        alert('No se pudo identificar el objeto seleccionado.');
        return;
      }

      if (selectedItem.source === 'ground') {
        try {
          await deleteGroundItemMutation.mutateAsync(selectedItem.id);
          closeEntityModal();
        } catch (err) {
          alert('Error al eliminar objeto del suelo: ' + (err.response?.data?.error || err.message));
        }
        return;
      }

      setLocalTiles(prev => prev.flatMap(t => {
        if (Number(t.coordinate) !== Number(entityModal.coordinate) || Number(t.layer) !== 1) {
          return [t];
        }

        if (Number(t.id_object) === Number(selectedItem.id_object || selectedItem.id)) {
          if (Number(t.terrain_type) >= 0) {
            const metadata = { ...(t.metadata || {}) };
            delete metadata.buried;
            return [{
              ...t,
              id_object: 0,
              is_buried: false,
              metadata
            }];
          }

          return [];
        }

        return [t];
      }));
      alert('Objeto eliminado del mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'portal') {
      const origin = Number(entityModal.data.coordenada_origen);
      const destSector = Number(entityModal.data.id_sector_destino);
      const destCoord = Number(entityModal.data.coordenada_destino);
      const currentSector = Number(selectedSectorId);

      setLocalPortals(prev => prev.filter(p => !(
        Number(p.coordenada_origen) === origin ||
        (destSector === currentSector &&
         Number(p.coordenada_origen) === destCoord &&
         Number(p.id_sector_destino) === currentSector &&
         Number(p.coordenada_destino) === origin)
      )));
      alert('Portal eliminado del mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
      return;
    }

    if (entityModal.type === 'event') {
      setLocalTiles(prev => prev.filter(t => !(t.coordinate === entityModal.coordinate && t.layer === 2)));
      alert('Evento eliminado del mapa local. Pulsa Guardar Mapa para persistir.');
      closeEntityModal();
    }
  };

  // Handler para clic en tile (para modos de entidades)
  const handleTileClick = (coordinate, mode) => {
    switch (mode) {
      case 'cities':
        {
          const existingCity = sectorCities.find(c => c.coordinate === coordinate);
          if (existingCity) {
            openEntityModal('city', coordinate, existingCity);
          } else {
            handlePlaceCity(coordinate);
          }
        }
        break;
      case 'enemies':
        {
          const existingEnemy = localTiles.find(t => t.coordinate === coordinate && t.layer === 2 && t.id_enemy_map > 0);
          if (existingEnemy) {
            openEntityModal('enemy', coordinate, existingEnemy);
          } else {
            handlePlaceEnemy(coordinate);
          }
        }
        break;
      case 'events':
        {
          const existingEvent = localTiles.find(t => t.coordinate === coordinate && t.layer === 2 && t.id_event > 0);
          if (existingEvent) {
            openEntityModal('event', coordinate, existingEvent);
            break;
          }
          const alreadyPlacedEvent = selectedMissionEventId
            ? localTiles.find(t => t.layer === 2 && Number(t.id_event) === Number(selectedMissionEventId))
            : null;
          if (alreadyPlacedEvent) {
            const placedInfo = placedMissionEventMap.get(Number(selectedMissionEventId));
            alert(`El evento #${selectedMissionEventId} ya esta colocado${placedInfo ? `${placedInfo.id_sector ? ` en el sector ${placedInfo.id_sector}` : ''}${placedInfo.coordinate !== undefined ? `, coord ${placedInfo.coordinate}` : ''}` : ''}. Usa otro evento o edita el existente.`);
            break;
          }
          if (!selectedMissionEventId) {
            alert('Primero selecciona un evento de misión en el panel lateral');
            break;
          }
          setLocalTiles(prev => {
            const filtered = prev.filter(t => !(t.coordinate === coordinate && t.layer === 2));
            return [
              ...filtered,
              {
                coordinate,
                layer: 2,
                terrain_type: -1,
                id_object: 0,
                id_enemy_map: 0,
                id_event: selectedMissionEventId,
                is_buried: false,
                metadata: {}
              }
            ];
          });
          alert(`Evento #${selectedMissionEventId} colocado en coordenada ${coordinate}. Pulsa Guardar Mapa para persistir.`);
          setSelectedMissionEventId(null);
        }
        break;
      case 'objects':
        {
          const existingObjects = renderedObjectInstances.filter(t => Number(t.coordinate) === Number(coordinate));
          if (existingObjects.length > 0) {
            openEntityModal('object', coordinate, {
              items: existingObjects,
              selectedObjectId: existingObjects[0]?.id || existingObjects[0]?.id_object || null
            });
          } else {
            handlePlaceObject(coordinate);
          }
        }
        break;
      case 'portals':
        {
          const existingPortal = localPortals.find(p => p.coordenada_origen === coordinate);
          if (existingPortal) {
            openEntityModal('portal', coordinate, existingPortal);
            break;
          }

          // Para portales nuevos: usar destino seleccionado en panel lateral
          if (selectedPortalTargetId && destinationCoordinate !== null && destinationCoordinate !== undefined) {
            const newPortal = {
              id_sector_origen: selectedSectorId,
              coordenada_origen: coordinate,
              id_sector_destino: selectedPortalTargetId,
              coordenada_destino: destinationCoordinate
            };
            setLocalPortals(prev => [...prev, newPortal]);
            alert(`Portal creado desde coordenada ${coordinate} hacia sector ${selectedPortalTargetId}, coordenada ${destinationCoordinate}`);
            setSelectedPortalTargetId(null);
            setDestinationCoordinate(null);
          } else {
            alert('Primero selecciona un sector destino y coordenada de destino en el panel lateral');
          }
          break;
        }
      default:
        break;
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setItemForm({
      name: '',
      description: '',
      weight: 0,
      power: 0,
      item_type: 3,
      image: 'default.png',
      level_required: 1,
      value: 0,
      metadata: {}
    });
  };

  const resetEnemyForm = () => {
    setEditingEnemy(null);
    setEnemyForm({
      name: '',
      level: 1,
      v_head: 20,
      v_torso: 40,
      v_arm_l: 20,
      v_arm_r: 20,
      v_leg_l: 30,
      v_leg_r: 30,
      image: 'enemy1.png',
      experience_reward: 10,
      money_reward: 5,
      metadata: {}
    });
  };

  const resetCityForm = () => {
    setEditingCity(null);
    setCityForm({
      name: '',
      coordinate: null,
      id_sector: null,
      image: 'city1.png',
      clan: 0,
      city_type: 0
    });
  };

  const resetSectorForm = () => {
    setEditingSector(null);
    setSectorForm({
      name: '',
      width: 20,
      height: 15,
      description: '',
      image: '',
      sector_type: 0,
      temperature: 20,
      climate: 'templado',
      tileset_id: 'overworld_v1'
    });
  };

  const handleCitySubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...cityForm,
      name: String(cityForm.name || '').trim(),
      coordinate: Number.isInteger(Number(cityForm.coordinate)) ? Number(cityForm.coordinate) : null,
      id_sector: Number.isInteger(Number(cityForm.id_sector)) && Number(cityForm.id_sector) > 0 ? Number(cityForm.id_sector) : null,
      clan: Number(cityForm.clan) || 0,
      city_type: Number(cityForm.city_type) || 0
    };

    if (!payload.name) {
      alert('El nombre de ciudad es obligatorio');
      return;
    }
    if (editingCity) {
      updateCityMutation.mutate({ id: editingCity.id, form: payload });
      return;
    }
    createCityMutation.mutate(payload);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setItemForm({
      ...item,
      item_type: item.item_type !== null ? Number(item.item_type) : 3,
      metadata: item.metadata || {}
    });
    setShowItemModal(true);
  };

  const handleEditEnemy = (enemy) => {
    setEditingEnemy(enemy);
    setEnemyForm({
      ...enemy,
      level: Number(enemy.level) || 1,
      v_head: Number(enemy.v_head) || 20,
      v_torso: Number(enemy.v_torso) || 40,
      v_arm_l: Number(enemy.v_arm_l) || 20,
      v_arm_r: Number(enemy.v_arm_r) || 20,
      v_leg_l: Number(enemy.v_leg_l) || 30,
      v_leg_r: Number(enemy.v_leg_r) || 30,
      image: enemy.image || 'enemy1.png',
      experience_reward: Number(enemy.experience_reward) || 10,
      money_reward: Number(enemy.money_reward) || 5,
      metadata: parseEnemyMetadata(enemy.metadata)
    });
    setShowEnemyModal(true);
  };

  const handleEditCity = (city) => {
    setEditingCity(city);
    setCityForm({
      ...city,
      coordinate: city.coordinate === null || city.coordinate === undefined ? null : Number(city.coordinate),
      id_sector: city.id_sector === null || city.id_sector === undefined ? null : Number(city.id_sector),
      clan: Number(city.clan) || 0,
      city_type: Number(city.city_type) || 0
    });
    setShowCityModal(true);
  };

  const handleEditSector = (sector) => {
    if (!sector) return;
    setEditingSector(sector);
    setSectorForm({
      ...sector,
      name: sector.name || '',
      width: Number(sector.width) || 20,
      height: Number(sector.height) || 15,
      description: sector.description || '',
      image: sector.image || '',
      sector_type: Number(sector.sector_type) || 0,
      temperature: Number(sector.temperature) || 20,
      climate: sector.climate || 'templado',
      tileset_id: sector.tileset_id || 'overworld_v1'
    });
    setShowSectorModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, form: itemForm });
    } else {
      createMutation.mutate(itemForm);
    }
  };

  const openMissionEventEditModal = (eventRow) => {
    let parsedMetadata = eventRow?.metadata;
    if (typeof parsedMetadata === 'string') {
      try { parsedMetadata = JSON.parse(parsedMetadata); } catch (_) { parsedMetadata = null; }
    }
    const normalized = {
      id_mission: Number(eventRow?.id_mission) || '',
      event_text: eventRow?.event_text || '',
      coordinate: Number(eventRow?.coordinate) || 0,
      id_sector: Number(eventRow?.id_sector) || 0,
      reward_money_give: Number(eventRow?.reward_money_give) || 0,
      reward_money_take: Number(eventRow?.reward_money_take) || 0,
      reward_item_give: Number(eventRow?.reward_item_give) || 0,
      reward_item_take: Number(eventRow?.reward_item_take) || 0,
      reward_quantity: Number(eventRow?.reward_quantity) || 1,
      image: eventRow?.image || '',
      is_active: Number(eventRow?.is_active ?? 1),
      metadata: {
        event_enemy_id: Number(parsedMetadata?.event_enemy_id) || 0,
        event_teleport_sector: Number(parsedMetadata?.event_teleport_sector) || 0,
        event_teleport_coordinate: Number(parsedMetadata?.event_teleport_coordinate) || 0,
        player_level_required: Number(parsedMetadata?.player_level_required) || 0
      }
    };
    setEditingMissionEvent(eventRow);
    setMissionEventEditForm(normalized);
    setShowMissionEventEditModal(true);
  };

  const handleSectorSubmit = (e) => {
    e.preventDefault();

    const normalized = {
      ...sectorForm,
      name: String(sectorForm.name || '').trim(),
      width: Number.isFinite(Number(sectorForm.width)) ? Number(sectorForm.width) : 20,
      height: Number.isFinite(Number(sectorForm.height)) ? Number(sectorForm.height) : 15,
      temperature: Number.isFinite(Number(sectorForm.temperature)) ? Number(sectorForm.temperature) : 20,
      sector_type: Number.isFinite(Number(sectorForm.sector_type)) ? Number(sectorForm.sector_type) : 0,
      tileset_id: sectorForm.tileset_id || 'overworld_v1'
    };

    if (!normalized.name) {
      alert('El nombre del sector es obligatorio');
      return;
    }

    if (editingSector) {
      updateSectorMutation.mutate({ id: editingSector.id, form: normalized });
      return;
    }

    createSectorMutation.mutate(normalized);
  };

  const stats = [
    { name: 'Usuarios Totales', value: adminStats?.stats?.users || 0, icon: Users, color: 'text-blue-500' },
    { name: 'Bots Registrados', value: adminStats?.stats?.bots?.total || 0, icon: Activity, color: 'text-green-500' },
    { name: 'Bots Activos', value: adminStats?.stats?.bots?.active || 0, icon: Package, color: 'text-yellow-500' },
  ];

  const tools = [
    { id: 'items', name: 'Gestor de Objetos', desc: 'Armas, medicina y equipo.', icon: Package, color: 'bg-indigo-500/20 text-indigo-400' },
    { id: 'rpg', name: 'RPG', desc: 'Tiles, huertos, edificios y catálogo visual.', icon: Gamepad2, color: 'bg-fuchsia-500/20 text-fuchsia-400' },
    { id: 'maps', name: 'Editor de Mapas', desc: 'Diseña sectores y cuadrículas.', icon: MapIcon, color: 'bg-emerald-500/20 text-emerald-400' },
    { id: 'tilesets', name: 'Tilesets', desc: 'Configura terrenos y sprites.', icon: Settings, color: 'bg-fuchsia-500/20 text-fuchsia-400' },
    { id: 'enemies', name: 'Fábrica de Bots', desc: 'Diseño y ensamblaje de enemigos.', icon: Database, color: 'bg-rose-500/20 text-rose-400' },
    { id: 'users', name: 'Usuarios', desc: 'Control de cuentas, bloqueos y limpieza total.', icon: Users, color: 'bg-sky-500/20 text-sky-400' },
    { id: 'clans', name: 'Panel de Clanes', desc: 'Jefes, miembros y actividad.', icon: Users, color: 'bg-cyan-500/20 text-cyan-400' },
    { id: 'settings', name: 'Configuración Global', desc: 'Variables del mundo.', icon: Settings, color: 'bg-amber-500/20 text-amber-400' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b1e] text-slate-200">
      {/* Sidebar Simulado */}
      <div className="flex">
        <aside className="w-64 bg-[#11132d] min-h-screen p-6 border-r border-slate-800/50">
          <div className="flex items-center gap-3 mb-10 px-2">
            <div className="w-8 h-8 bg-game-primary rounded-lg flex items-center justify-center font-bold text-white">M</div>
            <span className="text-xl font-bold tracking-tight text-white">MASTER PANEL</span>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'dashboard' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Activity size={18} />
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => setActiveTab('items')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'items' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Package size={18} />
              <span className="font-medium">Objetos</span>
            </button>
            <button
              onClick={() => setActiveTab('rpg')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'rpg' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Gamepad2 size={18} />
              <span className="font-medium">RPG</span>
            </button>
            <button
              onClick={() => setActiveTab('maps')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'maps' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <MapIcon size={18} />
              <span className="font-medium">Mapas</span>
            </button>
            <button
              onClick={() => setActiveTab('enemies')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'enemies' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Database size={18} />
              <span className="font-medium">Bots Enemigos</span>
            </button>
            <button
              onClick={() => setActiveTab('cities')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'cities' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <ArrowRight size={18} />
              <span className="font-medium">Ciudades</span>
            </button>
            <button
              onClick={() => setActiveTab('missions')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'missions' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Database size={18} />
              <span className="font-medium">Misiones/Eventos</span>
            </button>
            <button
              onClick={() => setActiveTab('clans')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'clans' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Users size={18} />
              <span className="font-medium">Clanes</span>
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'users' ? 'bg-game-primary/10 text-game-primary border border-game-primary/20' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <Users size={18} />
              <span className="font-medium">Usuarios</span>
            </button>

            <div className="pt-4 mt-4 border-t border-slate-800/50">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-slate-400 hover:bg-slate-800/50 hover:text-white"
              >
                <Home size={18} />
                <span className="font-medium">Dashboard Principal</span>
              </button>
            </div>
          </nav>
        </aside>

        <main className="flex-1 p-8 lg:p-12">
          {activeTab === 'dashboard' && (
            <div className="max-w-7xl mx-auto space-y-12">
              <header className="space-y-2">
                <h1 className="text-4xl font-extrabold text-white tracking-tight">Bienvenido, Master</h1>
                <p className="text-slate-400 text-lg">Estado global de Proyecto-V y herramientas rápidas.</p>
              </header>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.map((stat) => (
                  <div key={stat.name} className="bg-[#11132d] p-8 rounded-3xl border border-slate-800/50 hover:border-slate-700 transition-all shadow-xl shadow-black/20">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-2xl bg-slate-800/50 ${stat.color}`}>
                        <stat.icon size={24} />
                      </div>
                      <span className="text-green-400 text-sm font-semibold">+2.5%</span>
                    </div>
                    <div>
                      <h3 className="text-slate-400 font-medium mb-1">{stat.name}</h3>
                      <p className="text-4xl font-bold text-white tracking-tight">{isLoading ? '...' : stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tools Grid */}
              <section className="space-y-6">
                <h2 className="text-2xl font-bold text-white px-2">Herramientas del Sistema</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTab(tool.id)}
                      className="group bg-[#11132d] p-8 rounded-3xl border border-slate-800/50 hover:border-game-primary/30 text-left transition-all hover:bg-[#16183a] shadow-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-4">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${tool.color}`}>
                            <tool.icon size={28} />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white group-hover:text-game-primary transition-colors">{tool.name}</h3>
                            <p className="text-slate-400 mt-1">{tool.desc}</p>
                          </div>
                        </div>
                        <div className="p-2 rounded-full bg-slate-800/50 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                          <ArrowRight size={20} className="text-game-primary" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>

              {/* Logs Recientes Simulado */}
              <section className="bg-[#11132d] rounded-3xl border border-slate-800/50 overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-slate-800/50 flex items-center justify-between bg-slate-800/20">
                  <h3 className="font-bold text-white">Actividad Reciente del Master</h3>
                  <button className="text-sm font-semibold text-game-primary hover:underline">Ver todo</button>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {adminStats?.recentLogs?.length > 0 ? adminStats.recentLogs.map((log) => (
                      <div key={log.id} className="flex items-center gap-4 py-3 border-b border-slate-800/30 last:border-0">
                        <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-200"><span className="text-game-primary font-bold">{log.action}</span> on <span className="font-semibold">{log.target_type} #{log.target_id}</span></p>
                          <p className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    )) : (
                      <p className="text-slate-500 italic text-center py-6">No hay actividad reciente.</p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="flex justify-between items-end">
                <div className="space-y-2">
                  <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                    <ChevronLeft size={16} /> Volver al Dashboard
                  </button>
                  <h1 className="text-4xl font-extrabold text-white tracking-tight">Gestor de Objetos</h1>
                  <p className="text-slate-400 text-lg">Base de datos de equipamiento y consumibles.</p>
                </div>
                <button
                  onClick={() => setShowItemModal(true)}
                  className="bg-game-primary hover:bg-game-primary/80 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-lg shadow-game-primary/20 flex items-center gap-2"
                >
                  <Plus size={20} /> Crear Nuevo Objeto
                </button>
              </header>

              <div className="bg-[#11132d] rounded-3xl border border-slate-800/50 overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-800/20 border-b border-slate-800/50">
                    <tr>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">ID</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Objeto</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Nivel</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Valor</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingItems && (
                      <tr>
                        <td colSpan="6" className="p-12 text-center text-slate-500">Cargando objetos...</td>
                      </tr>
                    )}
                    {itemsData?.map((item) => (
                      <tr key={item.id} className="border-b border-slate-800/30 hover:bg-white/5 transition-colors">
                        <td className="p-6 font-mono text-slate-500 text-sm">#{item.id}</td>
                        <td className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700 overflow-hidden shrink-0">
                              {item.image && item.image !== 'default.png' ? (
                                <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${item.image}`} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <Package size={20} className="text-slate-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-white uppercase tracking-wide">{item.name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{item.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-6">
                          {(() => {
                            const val = item.item_type;
                            const legacyMap = { 'weapon': 0, 'medkit': 1, 'ammo': 2, 'object': 3, 'special': 4, 'mineral': 5, 'wood': 6, 'mineral/metal': 5 };
                            const numericType = (typeof val === 'string' && isNaN(val)) ? legacyMap[val] : parseInt(val);
                            return ITEM_TYPES.find(t => t.value === numericType)?.label || (val || 'Desconocido');
                          })()}
                        </td>
                        <td className="p-6">
                          <p className="text-white font-medium">Nv. {item.level_required}</p>
                        </td>
                        <td className="p-6 font-mono text-yellow-500 font-bold">
                          {item.value || 0} G
                        </td>
                        <td className="p-6">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id, 'item', item.name)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'rpg' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="flex justify-between items-end">
                <div className="space-y-2">
                  <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                    <ChevronLeft size={16} /> Volver al Dashboard
                  </button>
                  <div className="flex items-center gap-3">
                    <Gamepad2 size={34} className="text-fuchsia-400" />
                    <div>
                      <h1 className="text-4xl font-extrabold text-white tracking-tight">RPG Master</h1>
                      <p className="text-slate-400 text-lg">Sistema independiente del editor de mapas. Aquí se gestionan tilesets, ciudades y recursos del mundo RPG.</p>
                    </div>
                  </div>
                </div>
              </header>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#11132d] p-5 rounded-3xl border border-slate-800 shadow-xl">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Tilesets RPG</p>
                  <p className="mt-2 text-3xl font-bold text-white">{rpgTilesetsData.length || 0}</p>
                </div>
                <div className="bg-[#11132d] p-5 rounded-3xl border border-slate-800 shadow-xl">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Ciudades con RPG</p>
                  <p className="mt-2 text-3xl font-bold text-white">{rpgConfiguredCities.length || 0}</p>
                </div>
                <div className="bg-[#11132d] p-5 rounded-3xl border border-slate-800 shadow-xl">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Objetos especiales</p>
                  <p className="mt-2 text-3xl font-bold text-white">{rpgSpecialItems.length}</p>
                </div>
                <div className="bg-[#11132d] p-5 rounded-3xl border border-slate-800 shadow-xl">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Tileset activo</p>
                  <p className="mt-2 text-lg font-bold text-white truncate">{rpgTilesetsData.find((tileset) => tileset.is_active)?.slug || 'rpg_v1'}</p>
                </div>
              </div>

              <RpgWorldManager
                ref={rpgWorldManagerRef}
                tilesets={rpgTilesetsData}
                cities={adminCities}
                cityConfigs={rpgCitiesData}
                npcTemplates={rpgNpcTemplates}
                npcInstances={rpgNpcInstances}
                cropTransformations={rpgCropTransformations}
                items={itemsData}
                playerAppearance={rpgPlayerAppearance}
                isSavingTileset={createRpgTilesetMutation.isLoading || updateRpgTilesetMutation.isLoading}
                isSavingCity={saveRpgCityConfigMutation.isLoading}
                isSavingCityMap={saveRpgCityMapMutation.isLoading}
                isSavingNpcTemplate={createRpgNpcTemplateMutation.isLoading || updateRpgNpcTemplateMutation.isLoading}
                isSavingNpcInstance={createRpgCityNpcMutation.isLoading || updateRpgCityNpcMutation.isLoading}
                isSavingCropTransformation={createRpgCropTransformationMutation.isLoading || updateRpgCropTransformationMutation.isLoading}
                isSavingPlayerAppearance={saveRpgPlayerAppearanceMutation.isLoading}
                onOpenImageGallery={(target) => {
                  if (target === 'tileset') setGalleryTarget('rpg-tileset');
                  else if (target === 'city-config') setGalleryTarget('rpg-city-config');
                  else if (target === 'npc-template') setGalleryTarget('rpg-npc-template');
                  else if (target === 'player-appearance') setGalleryTarget('rpg-player-appearance');
                  else setGalleryTarget('rpg-city-config');
                  setShowGallery(true);
                }}
                onSaveTileset={(form) => {
                  if (form.id) {
                    updateRpgTilesetMutation.mutate({ id: form.id, form });
                  } else {
                    createRpgTilesetMutation.mutate(form);
                  }
                }}
                onDeleteTileset={(id) => {
                  if (window.confirm('¿Eliminar este tileset RPG?')) {
                    deleteRpgTilesetMutation.mutate(id);
                  }
                }}
                onSaveCity={(cityId, form) => {
                  saveRpgCityConfigMutation.mutate({ cityId, form });
                }}
                onSaveCityMap={(cityId, form) => {
                  saveRpgCityMapMutation.mutate({ cityId, form });
                }}
                onSaveNpcTemplate={(form) => {
                  if (form.id) {
                    updateRpgNpcTemplateMutation.mutate({ id: form.id, form });
                  } else {
                    createRpgNpcTemplateMutation.mutate(form);
                  }
                }}
                onDeleteNpcTemplate={(id) => {
                  if (window.confirm('¿Eliminar esta plantilla NPC?')) {
                    deleteRpgNpcTemplateMutation.mutate(id);
                  }
                }}
                onSaveNpcInstance={(form) => {
                  if (form.id) {
                    updateRpgCityNpcMutation.mutate({ id: form.id, form });
                  } else {
                    createRpgCityNpcMutation.mutate(form);
                  }
                }}
                onDeleteNpcInstance={(id) => {
                  if (window.confirm('¿Eliminar este NPC?')) {
                    deleteRpgCityNpcMutation.mutate(id);
                  }
                }}
                onSavePlayerAppearance={(form) => {
                  saveRpgPlayerAppearanceMutation.mutate(form);
                }}
                onSaveCropTransformation={(form) => {
                  if (form.id) {
                    updateRpgCropTransformationMutation.mutate({ id: form.id, form });
                  } else {
                    createRpgCropTransformationMutation.mutate(form);
                  }
                }}
                onDeleteCropTransformation={(id) => {
                  if (window.confirm('¿Eliminar esta regla de cultivo?')) {
                    deleteRpgCropTransformationMutation.mutate(id);
                  }
                }}
                onDeleteCity={(cityId) => {
                  if (window.confirm('¿Eliminar la configuración RPG de esta ciudad?')) {
                    deleteRpgCityConfigMutation.mutate(cityId);
                  }
                }}
              />

              <section className="bg-[#11132d] p-6 rounded-3xl border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                  <Leaf className="text-emerald-400" size={22} />
                  <div>
                    <h2 className="text-xl font-bold text-white">Inventario RPG rápido</h2>
                    <p className="text-sm text-slate-400">Estos objetos especiales vendrán después del mapa RPG y se usarán en huertos, tiendas y talleres.</p>
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-slate-800">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-800/20 border-b border-slate-800/50">
                      <tr>
                        <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-widest">ID</th>
                        <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Objeto</th>
                        <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</th>
                        <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Imagen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rpgSpecialItems.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="p-6 text-center text-slate-500">No hay objetos especiales todavía.</td>
                        </tr>
                      ) : rpgSpecialItems.map((item) => (
                        <tr key={item.id} className="border-b border-slate-800/30 hover:bg-white/5">
                          <td className="p-4 font-mono text-slate-500">#{item.id}</td>
                          <td className="p-4">
                            <div className="font-bold text-white">{item.name}</div>
                            <div className="text-xs text-slate-500 truncate max-w-xs">{item.description}</div>
                          </td>
                          <td className="p-4 text-slate-300">
                            {ITEM_TYPES.find((type) => type.value === Number(item.item_type))?.label || item.item_type}
                          </td>
                          <td className="p-4 text-slate-300">
                            {item.image || 'Sin imagen'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'maps' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {!selectedSectorId ? (
                <>
                  <header className="flex justify-between items-end">
                    <div className="space-y-2">
                      <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                        <ChevronLeft size={16} /> Volver al Dashboard
                      </button>
                      <h1 className="text-4xl font-extrabold text-white tracking-tight">Editor de Mapas</h1>
                      <p className="text-slate-400 text-lg">Configuración de sectores, clima y coordenadas.</p>
                    </div>
                    <button
                      onClick={() => {
                        resetSectorForm();
                        setShowSectorModal(true);
                      }}
                      className="bg-game-primary hover:bg-game-primary/80 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-lg shadow-game-primary/20 flex items-center gap-2"
                    >
                      <Plus size={20} /> Crear Nuevo Sector
                    </button>
                  </header>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {isLoadingSectors ? (
                      <div className="col-span-full py-12 text-center text-slate-500">Cargando sectores...</div>
                    ) : (sectorsData?.length > 0 ? sectorsData.map((sector) => (
                      <div key={sector.id} className="group bg-[#11132d] rounded-3xl border border-slate-800/50 overflow-hidden hover:border-game-primary/30 transition-all shadow-xl">
                        <div className="aspect-video bg-slate-900 flex items-center justify-center relative overflow-hidden">
                          {sector.image ? (
                            <img src={`/img/sector/info/${sector.image}`} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" alt={sector.name} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-slate-800">
                              <MapIcon size={48} className="text-slate-600" />
                            </div>
                          )}
                          <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-xs font-bold text-white border border-white/10 uppercase">
                            {sector.sector_type === 0 ? 'Superficie' : 'Subterráneo'}
                          </div>
                        </div>
                        <div className="p-6 space-y-4">
                          <div>
                            <h3 className="text-xl font-bold text-white group-hover:text-game-primary transition-colors">{sector.name}</h3>
                            <p className="text-slate-400 text-sm line-clamp-1 mt-1">{sector.description || 'Sin descripción.'}</p>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-slate-500">
                              <Grid size={14} className="text-blue-400" />
                              <span>{sector.width}x{sector.height}</span>
                            </div>
                            <button
                              onClick={() => setSelectedSectorId(sector.id)}
                              className="text-game-primary font-bold hover:underline flex items-center gap-1 transition-all"
                            >
                              Editar Visual <ArrowRight size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(sector.id, 'sector', sector.name)}
                              className="p-2 rounded-lg bg-slate-900/50 text-slate-500 hover:text-rose-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-full py-12 text-center text-slate-500 bg-slate-900/20 rounded-3xl border border-dashed border-slate-800">
                        No hay sectores configurados.
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="animate-in fade-in zoom-in-95 duration-500 flex flex-col gap-6">
                  <header className="flex justify-between items-center bg-[#11132d] p-6 rounded-3xl border border-slate-800">
                    <div className="flex items-center gap-4">
                      <button onClick={() => setSelectedSectorId(null)} className="p-3 bg-slate-800 hover:bg-slate-700 rounded-2xl transition-all text-slate-400">
                        <ChevronLeft size={20} />
                      </button>
                      <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">{sectorDetail?.sector?.name}</h2>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest px-2 py-1 bg-slate-900 rounded-md">
                            {sectorDetail?.sector?.width}x{sectorDetail?.sector?.height}
                          </span>
                          <span className="text-xs font-bold text-game-primary uppercase tracking-widest px-2 py-1 bg-game-primary/10 rounded-md">
                            Capa Activa: {activeLayer}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => handleEditSector(sectorDetail?.sector)}
                        className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2"
                      >
                        <Edit2 size={20} /> Editar Sector
                      </button>
                      <button
                        onClick={() => setActiveTab('tilesets')}
                        className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold py-3 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2"
                      >
                        <Settings size={20} /> Tilesets
                      </button>
                      <button
                        onClick={() => setShowMapTestModal(true)}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2"
                      >
                        Probar Mapa
                      </button>
                      <button
                        onClick={() => updateGridMutation.mutate({ id: selectedSectorId, tiles: localTiles, portals: localPortals })}
                        disabled={updateGridMutation.isLoading}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2 group"
                      >
                        <Save size={20} className="group-hover:scale-110 transition-transform" /> {updateGridMutation.isLoading ? 'Guardando...' : 'Guardar Mapa'}
                      </button>
                      <button
                        onClick={() => generateWorldEntitiesMutation.mutate({ id: selectedSectorId, payload: { replaceGenerated: true } })}
                        disabled={generateWorldEntitiesMutation.isLoading}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2"
                      >
                        <RefreshCcw size={20} className={generateWorldEntitiesMutation.isLoading ? 'animate-spin' : ''} />
                        {generateWorldEntitiesMutation.isLoading ? 'Generando...' : 'Generar Entidades'}
                      </button>
                    </div>
                  </header>

                  <div className="flex gap-6 h-[75vh]">
                    <div className="flex-1">
                      <MapCanvas
                        sector={sectorDetail?.sector}
                        tiles={localTiles}
                        tilesetConfig={tilesetOverworldConfig}
                        activeLayer={activeLayer}
                        selectedTile={selectedDrawingTile}
                        isDrawing={true}
                        onTilePaint={handleTilePaint}
                        onTileErase={handleTileErase}
                        // Nuevas props para entidades
                        cities={sectorCities}
                        enemies={enemyInstances}
                        objects={renderedObjectInstances}
                        events={eventInstances}
                        portals={localPortals}
                        editorMode={editorMode}
                        onClickTile={handleTileClick}
                        onHoverEventChange={(eventId) => {
                          setHoveredMapEventId(eventId);
                          if (eventId) setSelectedMissionEventId(eventId);
                        }}
                      />
                    </div>
                    <TileSelector
                        activeLayer={activeLayer}
                        setActiveLayer={setActiveLayer}
                        selectedTile={selectedDrawingTile}
                        setSelectedTile={setSelectedDrawingTile}
                        editorMode={editorMode}
                        setEditorMode={setEditorMode}
                        tilesetId={sectorDetail?.sector?.tileset_id || 'overworld_v1'}
                        tilesetConfig={tilesetOverworldConfig}
                        cities={adminCities}
                        selectedCityId={selectedCityId}
                        setSelectedCityId={setSelectedCityId}
                        enemies={enemiesData || []}
                        selectedEnemyId={selectedEnemyId}
                        setSelectedEnemyId={setSelectedEnemyId}
                        enemyInstances={enemyInstances}
                        items={itemsData || []}
                        selectedItemId={selectedItemId}
                        setSelectedItemId={setSelectedItemId}
                        isBuried={isBuried}
                        setIsBuried={setIsBuried}
                        objectInstances={renderedObjectInstances}
                        missionEvents={missionEventsData || []}
                        isLoadingMissionEvents={isLoadingMissionEvents}
                        selectedMissionEventId={selectedMissionEventId}
                        setSelectedMissionEventId={setSelectedMissionEventId}
                        eventInstances={eventInstances}
                        placedMissionEventMap={placedMissionEventMap}
                        hoveredEventId={hoveredMapEventId}
                        sectors={sectorsData || []}
                        currentSectorId={selectedSectorId}
                        portals={localPortals}
                        selectedPortalTargetId={selectedPortalTargetId}
                        setSelectedPortalTargetId={setSelectedPortalTargetId}
                        destinationCoordinate={destinationCoordinate}
                        setDestinationCoordinate={setDestinationCoordinate}
                        onPlacePortal={handlePlacePortal}
                        onDeletePortal={handleDeletePortal}
                      />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tilesets' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="space-y-2">
                <button onClick={() => setActiveTab('maps')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                  <ChevronLeft size={16} /> Volver al mapa
                </button>
                <h1 className="text-4xl font-extrabold text-white tracking-tight">Configuración de Tilesets</h1>
                <p className="text-slate-400 text-lg">Ajusta terrenos, frames y autotiles sin ensuciar el editor de mapas.</p>
              </header>

              <TilesetConfigurator
                value={tilesetOverworldConfig}
                isSaving={updateSettingsMutation.isLoading}
                onSave={(config) => {
                  updateSettingsMutation.mutate({
                    tileset_overworld_v1: JSON.stringify(config)
                  });
                }}
              />
            </div>
          )}

          {activeTab === 'enemies' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="flex justify-between items-end">
                <div className="space-y-2">
                  <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                    <ChevronLeft size={16} /> Volver al Dashboard
                  </button>
                  <h1 className="text-4xl font-extrabold text-white tracking-tight">Fábrica de Bots</h1>
                  <p className="text-slate-400 text-lg">Configuración de chasis, estadísticas y recompensas.</p>
                </div>
                <button
                  onClick={() => { resetEnemyForm(); setShowEnemyModal(true); }}
                  className="bg-game-primary hover:bg-game-primary/80 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-lg shadow-game-primary/20 flex items-center gap-2"
                >
                  <Plus size={20} /> Nuevo Bot
                </button>
              </header>

              <div className="bg-[#11132d] rounded-3xl border border-slate-800/50 overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-800/20 border-b border-slate-800/50">
                    <tr>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">ID</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Enemigo</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Nivel</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Vida Total</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Premios</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingEnemies ? (
                      <tr>
                        <td colSpan="6" className="p-12 text-center text-slate-500">Cargando bots...</td>
                      </tr>
                    ) : enemiesData?.map((enemy) => (
                      <tr key={enemy.id} className="border-b border-slate-800/30 hover:bg-white/5 transition-colors">
                        <td className="p-6 font-mono text-slate-500 text-sm">#{enemy.id}</td>
                        <td className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center border border-red-500/20 overflow-hidden shrink-0">
                              {enemy.image && enemy.image !== 'bot.png' && enemy.image !== 'enemy1.png' ? (
                                <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${enemy.image}`} alt={enemy.name} className="w-full h-full object-cover" />
                              ) : (
                                <Activity size={20} className="text-red-400" />
                              )}
                            </div>
                            <p className="font-bold text-white uppercase tracking-wide">{enemy.name}</p>
                          </div>
                        </td>
                        <td className="p-6 text-white font-medium">Nv. {enemy.level}</td>
                        <td className="p-6">
                          <div className="flex flex-col gap-1">
                            <span className="text-white font-bold">{enemy.v_torso + enemy.v_head + enemy.v_arm_l + enemy.v_arm_r + enemy.v_leg_l + enemy.v_leg_r} HP</span>
                            <span className="text-xs text-slate-500">Torso: {enemy.v_torso} | Cabeza: {enemy.v_head}</span>
                          </div>
                        </td>
                        <td className="p-6">
                          <div className="flex flex-col text-xs font-bold">
                            <span className="text-yellow-500">{enemy.money_reward} G</span>
                            <span className="text-blue-400">{enemy.experience_reward} EXP</span>
                          </div>
                        </td>
                        <td className="p-6">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditEnemy(enemy)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(enemy.id, 'enemy', enemy.name)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'cities' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="flex justify-between items-end">
                <div className="space-y-2">
                  <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                    <ChevronLeft size={16} /> Volver al Dashboard
                  </button>
                  <h1 className="text-4xl font-extrabold text-white tracking-tight">Gestor de Ciudades</h1>
                  <p className="text-slate-400 text-lg">Asentamientos, clanes y capitales.</p>
                </div>
                <button
                  onClick={() => { resetCityForm(); setShowCityModal(true); }}
                  className="bg-game-primary hover:bg-game-primary/80 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-lg shadow-game-primary/20 flex items-center gap-2"
                >
                  <Plus size={20} /> Crear Nueva Ciudad
                </button>
              </header>

              <div className="bg-[#11132d] rounded-3xl border border-slate-800/50 overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-800/20 border-b border-slate-800/50">
                    <tr>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">ID</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Ciudad</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Ubicación</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Clan</th>
                      <th className="p-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingCities ? (
                      <tr>
                        <td colSpan="6" className="p-12 text-center text-slate-500">Cargando ciudades...</td>
                      </tr>
                    ) : adminCities.map((city) => (
                      <tr key={city.id} className="border-b border-slate-800/30 hover:bg-white/5 transition-colors">
                        <td className="p-6 font-mono text-slate-500 text-sm">#{city.id}</td>
                        <td className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg overflow-hidden border border-emerald-500/30 bg-slate-900/80 shrink-0">
                              <img
                                src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${city.image}`}
                                alt={city.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                            <p className="font-bold text-white uppercase tracking-wide">{city.name}</p>
                          </div>
                        </td>
                        <td className="p-6">
                          <span className="text-xs font-bold text-slate-300 uppercase py-1 px-3 bg-slate-800 rounded-full">
                            {CITY_TYPE_LABELS[Number(city.city_type)] || 'Campamento'}
                          </span>
                        </td>
                        <td className="p-6 text-slate-400 font-medium">
                          Sec: {city.id_sector} | Coord: {city.coordinate}
                        </td>
                        <td className="p-6">
                          {Number(city.clan) === 0 ? (
                            <span className="text-slate-300 font-bold tracking-tight">Neutral</span>
                          ) : (
                            <span className="text-yellow-500 font-bold tracking-tight">
                              {clansById.get(Number(city.clan))?.name || `Clan #${city.clan}`}
                            </span>
                          )}
                        </td>
                        <td className="p-6">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditCity(city)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(city.id, 'city', city.name)}
                              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'missions' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="space-y-2">
                <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                  <ChevronLeft size={16} /> Volver al Dashboard
                </button>
                <h1 className="text-4xl font-extrabold text-white tracking-tight">Misiones y Eventos</h1>
                <p className="text-slate-400 text-lg">Gestión profesional por bloques: primero misión, después sus eventos.</p>
              </header>

              {!selectedMissionForEdit && (
                <>
                  <section className="bg-[#11132d] rounded-3xl border border-slate-800/50 p-6 space-y-4">
                    <h2 className="text-xl font-bold text-white">Crear Misión</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input value={missionForm.name} onChange={(e) => setMissionForm({ ...missionForm, name: e.target.value })} placeholder="Nombre misión" className="bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                      <input type="number" value={missionForm.level_required} onChange={(e) => setMissionForm({ ...missionForm, level_required: Number(e.target.value) })} placeholder="Nivel requerido" className="bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                      <input type="number" value={missionForm.reward_experience} onChange={(e) => setMissionForm({ ...missionForm, reward_experience: Number(e.target.value) })} placeholder="EXP recompensa" className="bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                    </div>
                    <textarea value={missionForm.description} onChange={(e) => setMissionForm({ ...missionForm, description: e.target.value })} placeholder="Descripción" rows={2} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                    <button onClick={() => createMissionMutation.mutate(missionForm)} className="bg-game-primary hover:bg-game-primary/80 text-white font-bold py-2 px-4 rounded-xl">Crear misión</button>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-white">Misiones por bloques</h2>
                    {isLoadingMissions ? (
                      <p className="text-slate-500 text-sm">Cargando misiones...</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {missionsData.map(m => (
                          <article key={m.id} className="bg-[#11132d] rounded-2xl border border-slate-800/50 p-5 space-y-3">
                            <div className="flex items-center justify-between">
                              <h3 className="text-white font-bold text-lg">#{m.id} {m.name}</h3>
                              <span className="text-xs bg-slate-800 px-2 py-1 rounded text-cyan-300">Nv.{m.level_required}</span>
                            </div>
                            <p className="text-slate-400 text-sm min-h-[40px]">{m.description || 'Sin descripción'}</p>
                            <div className="flex items-center justify-between pt-2">
                              <button
                                onClick={() => {
                                  setSelectedMissionForEdit(m);
                                  setMissionEventForm(prev => ({ ...prev, id_mission: m.id }));
                                }}
                                className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-3 py-2 rounded-xl text-sm"
                              >
                                Modificar misión
                              </button>
                              <button onClick={() => handleDelete(m.id, 'mission', m.name)} className="text-rose-400 text-xs font-bold">Eliminar</button>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}

              {selectedMissionForEdit && (
                <section className="space-y-5">
                  <div className="flex items-center justify-between bg-[#11132d] rounded-2xl border border-slate-800/50 p-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white">Editar misión #{selectedMissionForEdit.id}: {selectedMissionForEdit.name}</h2>
                      <p className="text-slate-400 text-sm">Aquí gestionas solo los eventos de esta misión.</p>
                    </div>
                    <button onClick={() => setSelectedMissionForEdit(null)} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
                      Volver a misiones
                    </button>
                  </div>

                  <section className="bg-[#11132d] rounded-3xl border border-slate-800/50 p-6 space-y-4">
                    <h3 className="text-xl font-bold text-white">Crear Evento para esta misión</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Misión</label>
                        <input value={`#${selectedMissionForEdit.id} ${selectedMissionForEdit.name}`} disabled className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-xl p-3 text-slate-300" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Imagen (opcional)</label>
                        <div className="mt-1 flex gap-2">
                          <input type="text" value={missionEventForm.image} onChange={(e) => setMissionEventForm({ ...missionEventForm, image: e.target.value })} placeholder="Imagen del evento" className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                          <button type="button" onClick={() => { setGalleryTarget('mission-event'); setShowGallery(true); }} className="px-4 py-2 bg-slate-700 hover:bg-game-primary text-white rounded-xl text-sm font-bold">Galería</button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Texto del evento</label>
                      <textarea value={missionEventForm.event_text} onChange={(e) => setMissionEventForm({ ...missionEventForm, event_text: e.target.value, id_mission: selectedMissionForEdit.id })} placeholder="Texto narrativo del evento" rows={3} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                    </div>

                    {missionEventForm.image && (
                      <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${missionEventForm.image}`} alt="preview evento" className="h-16 rounded-xl object-cover border border-slate-700" />
                    )}

                    <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-4 space-y-3">
                      <h4 className="text-sm font-bold text-cyan-200">Acciones del evento</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-cyan-100">Dar suma de dinero</label>
                          <input type="number" value={missionEventForm.reward_money_give} onChange={(e) => setMissionEventForm({ ...missionEventForm, reward_money_give: Number(e.target.value) || 0 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Dar un objeto</label>
                          <select value={missionEventForm.reward_item_give || 0} onChange={(e) => setMissionEventForm({ ...missionEventForm, reward_item_give: Number(e.target.value) || 0 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                            <option value={0}>-- Sin objeto --</option>
                            {isLoadingItems && <option value={0} disabled>Cargando objetos...</option>}
                            {(itemsData || []).map(item => (
                              <option key={item.id} value={item.id}>
                                #{item.id} {item.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Cantidad de objeto</label>
                          <input type="number" min={1} value={missionEventForm.reward_quantity} onChange={(e) => setMissionEventForm({ ...missionEventForm, reward_quantity: Number(e.target.value) || 1 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Poner enemigo</label>
                          <select value={missionEventForm.metadata?.event_enemy_id || 0} onChange={(e) => setMissionEventForm({ ...missionEventForm, metadata: { ...missionEventForm.metadata, event_enemy_id: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                            <option value={0}>-- Sin enemigo --</option>
                            {isLoadingEnemies && <option value={0} disabled>Cargando enemigos...</option>}
                            {(enemiesData || []).map(enemy => (
                              <option key={enemy.id} value={enemy.id}>
                                #{enemy.id} {enemy.name} (Nv.{enemy.level})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Transportar a sector</label>
                          <select value={missionEventForm.metadata?.event_teleport_sector || 0} onChange={(e) => setMissionEventForm({ ...missionEventForm, metadata: { ...missionEventForm.metadata, event_teleport_sector: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                            <option value={0}>-- Sin transporte --</option>
                            {isLoadingSectors && <option value={0} disabled>Cargando sectores...</option>}
                            {(sectorsData || []).map(sector => (
                              <option key={sector.id} value={sector.id}>
                                #{sector.id} {sector.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Posición destino (coordenada)</label>
                          <input type="number" min={0} value={missionEventForm.metadata?.event_teleport_coordinate || 0} onChange={(e) => setMissionEventForm({ ...missionEventForm, metadata: { ...missionEventForm.metadata, event_teleport_coordinate: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                        </div>
                        <div>
                          <label className="text-xs text-cyan-100">Nivel del jugador</label>
                          <input
                            type="number"
                            min={0}
                            value={missionEventForm.metadata?.player_level_required || 0}
                            onChange={(e) => setMissionEventForm({
                              ...missionEventForm,
                              metadata: {
                                ...missionEventForm.metadata,
                                player_level_required: Number(e.target.value) || 0
                              }
                            })}
                            className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                            placeholder="0 = sin nivel obligatorio"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => createMissionEventMutation.mutate({ ...missionEventForm, id_mission: selectedMissionForEdit.id })}
                      className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 px-4 rounded-xl"
                    >
                      Crear evento de esta misión
                    </button>
                  </section>

                  <section className="bg-[#11132d] rounded-3xl border border-slate-800/50 p-6 space-y-3">
                    <h3 className="text-xl font-bold text-white">Eventos de la misión #{selectedMissionForEdit.id}</h3>
                    <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                      {isLoadingMissionEvents ? <p className="text-slate-500 text-sm">Cargando eventos...</p> : missionEventsForSelectedMission.map(ev => (
                        <div key={ev.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                          <span className="text-sm text-slate-200">#{ev.id} {ev.event_text?.slice(0, 90) || 'Evento'}</span>
                          <div className="flex items-center gap-3">
                            <button onClick={() => openMissionEventEditModal(ev)} className="text-cyan-300 text-xs font-bold">Modificar</button>
                            <button onClick={() => handleDelete(ev.id, 'mission-event', `Evento ${ev.id}`)} className="text-rose-400 text-xs font-bold">Eliminar</button>
                          </div>
                        </div>
                      ))}
                      {!isLoadingMissionEvents && missionEventsForSelectedMission.length === 0 && (
                        <p className="text-slate-500 text-sm">Esta misión todavía no tiene eventos.</p>
                      )}
                    </div>
                  </section>
                </section>
              )}
            </div>
          )}

          {activeTab === 'clans' && (
            <div className="max-w-7xl mx-auto space-y-6">
              <header className="space-y-2">
                <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                  <ChevronLeft size={16} /> Volver al Dashboard
                </button>
                <h1 className="text-5xl font-extrabold text-white tracking-tight">Panel de Clanes</h1>
                <p className="text-slate-400 text-lg">Selecciona jefes manualmente y audita la actividad del clan.</p>
              </header>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-1 bg-[#11132d] border border-cyan-500/30 rounded-3xl p-6 space-y-4">
                  <h2 className="text-2xl font-bold text-cyan-300">Selección de clan</h2>
                  <select
                    value={selectedAdminClanId || ''}
                    onChange={(e) => setSelectedAdminClanId(Number(e.target.value) || null)}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                  >
                    <option value="">-- Elegir clan --</option>
                    {(adminClansData || []).map((clan) => (
                      <option key={clan.id} value={clan.id}>
                        #{clan.id} {clan.name} ({clan.member_count} miembros)
                      </option>
                    ))}
                  </select>
                  {isLoadingAdminClans && <p className="text-slate-400 text-sm">Cargando clanes...</p>}
                </div>

                <div className="xl:col-span-2 bg-[#11132d] border border-cyan-500/30 rounded-3xl p-6 space-y-4">
                  <h2 className="text-2xl font-bold text-cyan-300">Asignar jefe</h2>
                  {!selectedAdminClanId ? (
                    <p className="text-slate-400">Selecciona un clan para ver miembros.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <select
                          value={selectedLeaderUserId}
                          onChange={(e) => setSelectedLeaderUserId(e.target.value)}
                          className="md:col-span-2 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        >
                          <option value="">-- Elegir miembro --</option>
                          {(adminClanMembersData?.members || []).map((member) => (
                            <option key={member.id_user} value={member.id_user}>
                              {member.username} ({member.email}) {Number(member.role) === 2 ? ' - jefe actual' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => {
                            if (!selectedAdminClanId || !selectedLeaderUserId) return;
                            setClanLeaderMutation.mutate({
                              clanId: selectedAdminClanId,
                              leaderUserId: Number(selectedLeaderUserId)
                            });
                          }}
                          disabled={!selectedLeaderUserId || setClanLeaderMutation.isLoading}
                          className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold py-3 rounded-xl"
                        >
                          Guardar jefe
                        </button>
                      </div>
                      {isLoadingAdminClanMembers && <p className="text-slate-400 text-sm">Cargando miembros...</p>}
                    </>
                  )}
                </div>
              </div>

              <section className="bg-[#11132d] border border-cyan-500/30 rounded-3xl p-6">
                <h2 className="text-2xl font-bold text-cyan-300 mb-4">Actividad del clan</h2>
                {!selectedAdminClanId ? (
                  <p className="text-slate-400">Selecciona un clan para ver actividad.</p>
                ) : isLoadingAdminClanActivity ? (
                  <p className="text-slate-400">Cargando actividad...</p>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
                    {(adminClanActivityData?.activity || []).length === 0 && (
                      <p className="text-slate-400">Sin actividad registrada todavía.</p>
                    )}
                    {(adminClanActivityData?.activity || []).map((evt) => (
                      <article key={evt.id} className="border border-slate-700/60 rounded-xl p-4 bg-slate-900/30">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm text-cyan-300 font-bold">{evt.action_key}</p>
                          <p className="text-xs text-slate-400">{new Date(evt.created_at).toLocaleString()}</p>
                        </div>
                        <p className="text-sm text-slate-300 mt-1">{evt.detail || 'Sin detalle'}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Actor: {evt.actor_username || '-'} | Objetivo: {evt.target_username || '-'}
                        </p>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="space-y-2">
                <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                  <ChevronLeft size={16} /> Volver al Dashboard
                </button>
                <h1 className="text-5xl font-extrabold text-white tracking-tight">Usuarios del Juego</h1>
                <p className="text-slate-400 text-lg">Controla cuentas, bloqueos, reinicios y limpieza total de cada jugador.</p>
              </header>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <section className="xl:col-span-4 bg-[#11132d] border border-sky-500/20 rounded-3xl p-6 space-y-6 shadow-2xl">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Total</p>
                      <p className="text-3xl font-bold text-white mt-1">{adminUsersSummary.total || 0}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Bloqueados</p>
                      <p className="text-3xl font-bold text-rose-300 mt-1">{adminUsersSummary.blocked || 0}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Con bot</p>
                      <p className="text-3xl font-bold text-cyan-300 mt-1">{adminUsersSummary.withBot || 0}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Sin bot</p>
                      <p className="text-3xl font-bold text-amber-300 mt-1">{adminUsersSummary.withoutBot || 0}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-bold text-slate-400 uppercase">Buscar</label>
                    <input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Usuario, email, bot o ID"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:border-game-primary focus:outline-none"
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-bold text-slate-400 uppercase">Filtro</label>
                    <select
                      value={userStatusFilter}
                      onChange={(e) => setUserStatusFilter(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                    >
                      <option value="all">Todos</option>
                      <option value="blocked">Bloqueados</option>
                      <option value="admins">Admins</option>
                      <option value="with-bot">Con bot</option>
                      <option value="without-bot">Sin bot</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-sky-300">Listado</h2>
                      {isLoadingAdminUsers && <span className="text-xs text-slate-500">Cargando...</span>}
                    </div>
                    <div className="max-h-[540px] overflow-auto space-y-2 pr-1">
                      {filteredAdminUsers.length === 0 && !isLoadingAdminUsers && (
                        <p className="text-slate-500 text-sm italic py-6 text-center">No hay usuarios para mostrar.</p>
                      )}
                      {filteredAdminUsers.map((user) => {
                        const isSelected = Number(user.id) === Number(selectedAdminUserId);
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => setSelectedAdminUserId(user.id)}
                            className={`w-full text-left rounded-2xl border p-4 transition-all ${isSelected ? 'border-game-primary bg-game-primary/10' : 'border-slate-800 bg-slate-900/30 hover:border-slate-700 hover:bg-slate-900/50'}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-bold text-white">{user.username}</p>
                                <p className="text-xs text-slate-500">{user.email}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                {user.is_blocked ? (
                                  <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Bloqueado</span>
                                ) : (
                                  <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Activo</span>
                                )}
                                {user.is_admin && (
                                  <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">Admin</span>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
                              <span>Bot: {user.bot?.name || 'Sin bot'}</span>
                              <span>•</span>
                              <span>Clan: {user.selected_clan_id || 'Ninguno'}</span>
                              <span>•</span>
                              <span>Último acceso: {user.last_login ? new Date(user.last_login).toLocaleString() : 'Nunca'}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                <section className="xl:col-span-8 bg-[#11132d] border border-sky-500/20 rounded-3xl p-6 shadow-2xl space-y-6">
                  {!selectedAdminUser ? (
                    <p className="text-slate-400">Selecciona un usuario para ver su ficha.</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h2 className="text-3xl font-bold text-white">{selectedAdminUser.username}</h2>
                          <p className="text-slate-400">{selectedAdminUser.email}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-full bg-slate-900/60 border border-slate-700 text-slate-300">
                            ID #{selectedAdminUser.id}
                          </span>
                          {selectedAdminUser.is_blocked ? (
                            <span className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300">
                              Bloqueado
                            </span>
                          ) : (
                            <span className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
                              Acceso permitido
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Bot</p>
                          <p className="text-white font-bold mt-1">{selectedAdminUser.bot?.name || 'Sin bot'}</p>
                          <p className="text-xs text-slate-500 mt-1">Activos: {selectedAdminUser.active_bot_count || 0}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Clan</p>
                          <p className="text-white font-bold mt-1">{selectedAdminUser.selected_clan_id || 'Ninguno'}</p>
                          <p className="text-xs text-slate-500 mt-1">Paso onboarding: {selectedAdminUser.onboarding_step}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Sesión</p>
                          <p className="text-white font-bold mt-1">{selectedAdminUser.last_login ? new Date(selectedAdminUser.last_login).toLocaleString() : 'Nunca'}</p>
                          <p className="text-xs text-slate-500 mt-1">Creado: {selectedAdminUser.created_at ? new Date(selectedAdminUser.created_at).toLocaleDateString() : '-'}</p>
                        </div>
                      </div>

                      <form
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        onSubmit={(e) => {
                          e.preventDefault();
                          updateUserMutation.mutate({
                            id: selectedAdminUser.id,
                            form: {
                              ...userForm,
                              selected_clan_id: userForm.selected_clan_id || null
                            }
                          });
                        }}
                      >
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Usuario</label>
                          <input
                            value={userForm.username}
                            onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Email</label>
                          <input
                            type="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">País</label>
                          <input
                            value={userForm.country}
                            onChange={(e) => setUserForm({ ...userForm, country: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Clan</label>
                          <select
                            value={userForm.selected_clan_id}
                            onChange={(e) => setUserForm({ ...userForm, selected_clan_id: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                          >
                            <option value="">Sin clan</option>
                            {adminClanOptions.map((clan) => (
                              <option key={clan.id} value={clan.id}>
                                #{clan.id} {clan.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Paso onboarding</label>
                          <select
                            value={userForm.onboarding_step}
                            onChange={(e) => setUserForm({ ...userForm, onboarding_step: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                          >
                            <option value="welcome">welcome</option>
                            <option value="clan">clan</option>
                            <option value="bot_setup">bot_setup</option>
                            <option value="completed">completed</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Estado</label>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300">
                              <input type="checkbox" checked={userForm.is_blocked} onChange={(e) => setUserForm({ ...userForm, is_blocked: e.target.checked })} />
                              Bloqueado
                            </label>
                            <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300">
                              <input type="checkbox" checked={userForm.is_confirmed} onChange={(e) => setUserForm({ ...userForm, is_confirmed: e.target.checked })} />
                              Confirmado
                            </label>
                            <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300">
                              <input type="checkbox" checked={userForm.is_admin} onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })} />
                              Admin
                            </label>
                            <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300">
                              <input type="checkbox" checked={userForm.intro_seen} onChange={(e) => setUserForm({ ...userForm, intro_seen: e.target.checked })} />
                              Intro vista
                            </label>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-400 uppercase">Progreso</label>
                          <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300">
                            <input
                              type="checkbox"
                              checked={userForm.has_completed_onboarding}
                              onChange={(e) => setUserForm({ ...userForm, has_completed_onboarding: e.target.checked })}
                            />
                            Onboarding completado
                          </label>
                        </div>

                        <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
                          <button
                            type="submit"
                            disabled={updateUserMutation.isLoading}
                            className="bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-2xl transition-all flex items-center gap-2"
                          >
                            <Save size={18} /> Guardar cambios
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (selectedAdminUser.is_blocked) {
                                unblockUserMutation.mutate(selectedAdminUser.id);
                              } else {
                                blockUserMutation.mutate(selectedAdminUser.id);
                              }
                            }}
                            disabled={blockUserMutation.isLoading || unblockUserMutation.isLoading}
                            className={`font-bold py-3 px-6 rounded-2xl transition-all border ${selectedAdminUser.is_blocked ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-rose-500/40 text-rose-300 bg-rose-500/10'}`}
                          >
                            {selectedAdminUser.is_blocked ? 'Desbloquear acceso' : 'Bloquear entrada'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`¿Reiniciar por completo a ${selectedAdminUser.username}? Se borrará su bot, inventario, progreso y relaciones.`)) {
                                resetUserMutation.mutate(selectedAdminUser.id);
                              }
                            }}
                            disabled={resetUserMutation.isLoading}
                            className="font-bold py-3 px-6 rounded-2xl transition-all border border-amber-500/40 text-amber-300 bg-amber-500/10"
                          >
                            Reinicio limpio
                          </button>
                          <button
                            type="button"
                          onClick={() => {
                              if (window.confirm(`¿Eliminar definitivamente a ${selectedAdminUser.username}? Esta acción borra el usuario y todos sus datos.`)) {
                                deleteUserMutation.mutate(selectedAdminUser.id);
                              }
                            }}
                            disabled={deleteUserMutation.isLoading}
                            className="font-bold py-3 px-6 rounded-2xl transition-all border border-rose-500/60 text-rose-200 bg-rose-500/20"
                          >
                            Eliminar totalmente
                          </button>
                        </div>
                      </form>

                      <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-4 text-sm text-slate-400">
                        <p className="font-bold text-slate-200 mb-1">Notas operativas</p>
                        <p>Bloquear la cuenta impide el login. El reinicio limpia el bot y toda la progresión para empezar de cero. El borrado total elimina la cuenta y sus datos asociados.</p>
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <header className="space-y-2">
                <button onClick={() => setActiveTab('dashboard')} className="text-game-primary text-sm font-bold flex items-center gap-2 hover:underline mb-2 transition-all">
                  <ChevronLeft size={16} /> Volver al Dashboard
                </button>
                <h1 className="text-4xl font-extrabold text-white tracking-tight">Configuración Global</h1>
                <p className="text-slate-400 text-lg">Control maestro del servidor y variables del mundo.</p>
              </header>

              <div className="grid grid-cols-1 gap-8">
                {/* Estado del Servidor */}
                <section className="bg-[#11132d] p-8 rounded-3xl border border-slate-800 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                    <Settings className="text-amber-400" size={24} />
                    <h2 className="text-xl font-bold text-white">Estado del Servidor</h2>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-slate-800">
                    <div>
                      <h3 className="font-bold text-white">Modo Mantenimiento</h3>
                      <p className="text-sm text-slate-500">Impide que los jugadores entren al juego si está activo.</p>
                    </div>
                    <button
                      onClick={() => setSettingsForm({ ...settingsForm, maintenance_mode: settingsForm.maintenance_mode === '1' ? '0' : '1' })}
                      className={`w-14 h-8 rounded-full transition-all relative ${settingsForm.maintenance_mode === '1' ? 'bg-rose-500' : 'bg-slate-700'}`}
                    >
                      <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${settingsForm.maintenance_mode === '1' ? 'left-7' : 'left-1'}`}></div>
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Mensaje del Día (MOTD)</label>
                    <textarea
                      value={settingsForm.motd || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, motd: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-4 text-white focus:border-game-primary focus:outline-none h-24"
                    />
                  </div>
                </section>

                {/* Multiplicadores */}
                <section className="bg-[#11132d] p-8 rounded-3xl border border-slate-800 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                    <TrendingUp className="text-emerald-400" size={24} />
                    <h2 className="text-xl font-bold text-white">Multiplicadores y Eventos</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Multiplicador de XP</label>
                      <div className="relative">
                        <input
                          type="number" step="0.1"
                          value={settingsForm.xp_multiplier || 1.0}
                          onChange={(e) => setSettingsForm({ ...settingsForm, xp_multiplier: e.target.value })}
                          className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white pl-10"
                        />
                        <TrendingUp className="absolute left-3 top-3.5 text-slate-500" size={18} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Multiplicador de Oro</label>
                      <div className="relative">
                        <input
                          type="number" step="0.1"
                          value={settingsForm.gold_multiplier || 1.0}
                          onChange={(e) => setSettingsForm({ ...settingsForm, gold_multiplier: e.target.value })}
                          className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white pl-10"
                        />
                        <BarChart3 className="absolute left-3 top-3.5 text-slate-500" size={18} />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Inicio de Jugador */}
                <section className="bg-[#11132d] p-8 rounded-3xl border border-slate-800 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                    <Users className="text-blue-400" size={24} />
                    <h2 className="text-xl font-bold text-white">Configuración del Nuevo Jugador</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Dinero Inicial</label>
                      <input
                        type="number"
                        value={settingsForm.starting_money || 100}
                        onChange={(e) => setSettingsForm({ ...settingsForm, starting_money: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Nivel Inicial</label>
                      <input
                        type="number"
                        value={settingsForm.starting_level || 1}
                        onChange={(e) => setSettingsForm({ ...settingsForm, starting_level: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      />
                    </div>
                  </div>
                </section>

                {/* IA Reclutamiento de Clanes */}
                <section className="bg-[#11132d] p-8 rounded-3xl border border-slate-800 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                    <Cpu className="text-fuchsia-400" size={24} />
                    <h2 className="text-xl font-bold text-white">IA de Reclutamiento (Clanes)</h2>
                  </div>
                  <p className="text-sm text-slate-400">
                    Configura claves y prioridad de fallback. Si falla el primer proveedor, se usa el siguiente.
                  </p>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Prioridad de proveedores</label>
                    <input
                      type="text"
                      value={settingsForm.ai_provider_priority || 'openai,groq,deepseek'}
                      onChange={(e) => setSettingsForm({ ...settingsForm, ai_provider_priority: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      placeholder="openai,groq,deepseek"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">OpenAI API Key</label>
                      <input
                        type="password"
                        value={settingsForm.ai_openai_api_key || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_openai_api_key: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="sk-..."
                      />
                      <label className="text-xs text-slate-500 uppercase">Modelo OpenAI</label>
                      <input
                        type="text"
                        value={settingsForm.ai_openai_model || 'gpt-4o-mini'}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_openai_model: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="gpt-4o-mini"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Groq API Key</label>
                      <input
                        type="password"
                        value={settingsForm.ai_groq_api_key || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_groq_api_key: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="gsk_..."
                      />
                      <label className="text-xs text-slate-500 uppercase">Modelo Groq</label>
                      <input
                        type="text"
                        value={settingsForm.ai_groq_model || 'llama-3.1-8b-instant'}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_groq_model: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="llama-3.1-8b-instant"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">DeepSeek API Key</label>
                      <input
                        type="password"
                        value={settingsForm.ai_deepseek_api_key || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_deepseek_api_key: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="sk-..."
                      />
                      <label className="text-xs text-slate-500 uppercase">Modelo DeepSeek</label>
                      <input
                        type="text"
                        value={settingsForm.ai_deepseek_model || 'deepseek-chat'}
                        onChange={(e) => setSettingsForm({ ...settingsForm, ai_deepseek_model: e.target.value })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                        placeholder="deepseek-chat"
                      />
                    </div>
                  </div>
                </section>

                <div className="flex gap-4">
                  <button
                    onClick={() => updateSettingsMutation.mutate(settingsForm)}
                    disabled={updateSettingsMutation.isLoading || isLoadingSettings}
                    className="flex-1 bg-game-primary hover:bg-game-primary/80 text-white font-bold py-4 rounded-2xl shadow-lg shadow-game-primary/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Save size={20} /> Guardar Configuración Global
                  </button>
                </div>

                {/* Mantenimiento Técnico */}
                <section className="bg-rose-500/5 p-8 rounded-3xl border border-rose-500/20 space-y-6">
                  <div className="flex items-center gap-3">
                    <Trash2 className="text-rose-500" size={24} />
                    <h2 className="text-xl font-bold text-white">Mantenimiento Técnico</h2>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 text-sm max-w-md">Elimina logs de batalla, transacciones y seguridad antiguos (más de 90-180 días) para optimizar la base de datos.</p>
                    <button
                      onClick={() => { if (confirm('¿Deseas ejecutar la limpieza de logs?')) cleanupMutation.mutate(); }}
                      disabled={cleanupMutation.isLoading}
                      className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/30 px-6 py-3 rounded-xl font-bold transition-all"
                    >
                      {cleanupMutation.isLoading ? 'Ejecutando...' : 'Ejecutar Limpieza'}
                    </button>
                  </div>
                </section>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* MODAL EDITAR EVENTO DE MISION */}
      {showMissionEventEditModal && missionEventEditForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-4xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-6 border-b border-slate-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Modificar Evento #{editingMissionEvent?.id}</h2>
              <button onClick={() => { setShowMissionEventEditModal(false); setEditingMissionEvent(null); setMissionEventEditForm(null); }} className="text-slate-500 hover:text-white">Cerrar</button>
            </header>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Misión</label>
                  <select value={missionEventEditForm.id_mission} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, id_mission: Number(e.target.value) || '' })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                    <option value="">-- Misión --</option>
                    {missionsData.map(m => <option key={m.id} value={m.id}>#{m.id} {m.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Imagen</label>
                  <div className="mt-1 flex gap-2">
                    <input type="text" value={missionEventEditForm.image || ''} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, image: e.target.value })} className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                    <button type="button" onClick={() => { setGalleryTarget('mission-event-edit'); setShowGallery(true); }} className="px-4 py-2 bg-slate-700 hover:bg-game-primary text-white rounded-xl text-sm font-bold">Galería</button>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Texto del evento</label>
                <textarea value={missionEventEditForm.event_text} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, event_text: e.target.value })} rows={3} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
              </div>
              <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-4 space-y-3">
                <h4 className="text-sm font-bold text-cyan-200">Acciones del evento</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-cyan-100">Dar suma de dinero</label>
                    <input type="number" value={missionEventEditForm.reward_money_give} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, reward_money_give: Number(e.target.value) || 0 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Dar un objeto</label>
                    <select value={missionEventEditForm.reward_item_give || 0} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, reward_item_give: Number(e.target.value) || 0 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                      <option value={0}>-- Sin objeto --</option>
                      {(itemsData || []).map(item => <option key={item.id} value={item.id}>#{item.id} {item.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Cantidad de objeto</label>
                    <input type="number" min={1} value={missionEventEditForm.reward_quantity} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, reward_quantity: Number(e.target.value) || 1 })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Poner enemigo</label>
                    <select value={missionEventEditForm.metadata?.event_enemy_id || 0} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, metadata: { ...missionEventEditForm.metadata, event_enemy_id: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                      <option value={0}>-- Sin enemigo --</option>
                      {(enemiesData || []).map(enemy => <option key={enemy.id} value={enemy.id}>#{enemy.id} {enemy.name} (Nv.{enemy.level})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Transportar a sector</label>
                    <select value={missionEventEditForm.metadata?.event_teleport_sector || 0} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, metadata: { ...missionEventEditForm.metadata, event_teleport_sector: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                      <option value={0}>-- Sin transporte --</option>
                      {(sectorsData || []).map(sector => <option key={sector.id} value={sector.id}>#{sector.id} {sector.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Posición destino (coordenada)</label>
                    <input type="number" min={0} value={missionEventEditForm.metadata?.event_teleport_coordinate || 0} onChange={(e) => setMissionEventEditForm({ ...missionEventEditForm, metadata: { ...missionEventEditForm.metadata, event_teleport_coordinate: Number(e.target.value) || 0 } })} className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-cyan-100">Nivel del jugador</label>
                    <input
                      type="number"
                      min={0}
                      value={missionEventEditForm.metadata?.player_level_required || 0}
                      onChange={(e) => setMissionEventEditForm({
                        ...missionEventEditForm,
                        metadata: {
                          ...missionEventEditForm.metadata,
                          player_level_required: Number(e.target.value) || 0
                        }
                      })}
                      className="mt-1 w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      placeholder="0 = sin nivel obligatorio"
                    />
                  </div>
                </div>
              </div>
            </div>
            <footer className="p-6 border-t border-slate-800 flex gap-3">
              <button onClick={() => { setShowMissionEventEditModal(false); setEditingMissionEvent(null); setMissionEventEditForm(null); }} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold py-3 rounded-xl">Cancelar</button>
              <button
                onClick={() => updateMissionEventMutation.mutate({ id: editingMissionEvent.id, form: missionEventEditForm })}
                disabled={updateMissionEventMutation.isLoading}
                className="flex-[2] bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-3 rounded-xl"
              >
                Guardar cambios
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* MODAL ENTIDAD EN MAPA (CIUDAD/ENEMIGO/OBJETO) */}
      {entityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-lg rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-6 border-b border-slate-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase tracking-tight">
                {entityModal.type === 'city' ? 'Editar Ciudad' : entityModal.type === 'enemy' ? 'Editar Enemigo' : entityModal.type === 'object' ? 'Editar Objeto' : entityModal.type === 'portal' ? 'Editar Portal' : 'Editar Evento'}
              </h2>
              <button
                onClick={closeEntityModal}
                className="text-slate-500 hover:text-white transition-colors"
              >
                Cerrar
              </button>
            </header>

            <div className="p-6 space-y-4">
              <div className="text-xs text-slate-500 uppercase tracking-widest">
                Coordenada: {entityModal.coordinate}
              </div>

              {entityModal.type === 'city' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Nombre</label>
                    <input
                      type="text"
                      value={entityModalForm.name || ''}
                      onChange={(e) => setEntityModalForm({ ...entityModalForm, name: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Tipo</label>
                      <select
                        value={entityModalForm.city_type ?? 0}
                        onChange={(e) => setEntityModalForm({ ...entityModalForm, city_type: Number(e.target.value) })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      >
                        <option value={0}>Campamento</option>
                        <option value={1}>Pueblo</option>
                        <option value={2}>Ciudad</option>
                        <option value={3}>Capital</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Clan</label>
                      <select
                        value={entityModalForm.clan ?? 0}
                        onChange={(e) => setEntityModalForm({ ...entityModalForm, clan: Number(e.target.value) })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      >
                        <option value={0}>Neutral</option>
                        {safeClansData.map((clan) => (
                          <option key={clan.id} value={clan.id}>
                            {clan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {entityModal.type === 'enemy' && (
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Tipo de Enemigo</label>
                  <select
                    value={entityModalForm.id_enemy_map ?? 0}
                    onChange={(e) => setEntityModalForm({ ...entityModalForm, id_enemy_map: Number(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                  >
                    {(enemiesData || []).map(enemy => (
                      <option key={enemy.id} value={enemy.id}>
                        {enemy.name} (Nv.{enemy.level})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {entityModal.type === 'object' && (
                <>
                  {objectModalItems.length > 1 && (
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400 uppercase">Objetos en la casilla</label>
                      <select
                        value={objectModalSelectedId || ''}
                        onChange={(e) => setEntityModalForm({ ...entityModalForm, selectedObjectId: Number(e.target.value) })}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      >
                        {objectModalItems.map((item) => {
                          const itemId = item.id || item.id_object;
                          const sourceLabel = item.source === 'ground' ? 'Suelo' : 'Mapa';
                          const qtyLabel = Number(item.quantity || 1) > 1 ? ` x${Number(item.quantity)}` : '';
                          return (
                            <option key={`${item.source || 'map'}-${itemId}`} value={itemId}>
                              {item.name || `Objeto ${itemId}`}{qtyLabel} · {sourceLabel}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  {objectModalSelectedItem && (
                    <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4 space-y-2 text-sm text-slate-300">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white">{objectModalSelectedItem.name || 'Objeto'}</span>
                        <span className="text-xs uppercase tracking-widest text-cyan-300">
                          {objectModalSelectedItem.source === 'ground' ? 'Suelo' : 'Mapa'}
                        </span>
                      </div>
                      <div>
                        Tipo: {ITEM_TYPES.find((type) => Number(type.value) === Number(objectModalSelectedItem.item_type || 0))?.label || 'Desconocido'}
                      </div>
                      {Number(objectModalSelectedItem.quantity || 1) > 1 && (
                        <div>Cantidad: {Number(objectModalSelectedItem.quantity)}</div>
                      )}
                      <div>Id: {objectModalSelectedItem.id || objectModalSelectedItem.id_object}</div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Objeto</label>
                    <select
                      value={entityModalForm.id_object ?? 0}
                      onChange={(e) => setEntityModalForm({ ...entityModalForm, id_object: Number(e.target.value) })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      disabled={objectModalSelectedItem?.source === 'ground'}
                    >
                      {(itemsData || []).map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={!!entityModalForm.is_buried}
                      onChange={(e) => setEntityModalForm({ ...entityModalForm, is_buried: e.target.checked })}
                      className="accent-game-primary"
                      disabled={objectModalSelectedItem?.source === 'ground'}
                    />
                    Enterrado
                  </label>
                  {objectModalSelectedItem?.source === 'ground' && (
                    <p className="text-xs text-amber-300">
                      Los objetos del suelo son eliminables, pero no se editan desde este modal.
                    </p>
                  )}
                </>
              )}

              {entityModal.type === 'portal' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Sector Destino</label>
                    <select
                      value={entityModalForm.id_sector_destino ?? 0}
                      onChange={(e) => setEntityModalForm({ ...entityModalForm, id_sector_destino: Number(e.target.value) })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                    >
                      {(sectorsData || [])
                        .map(sector => (
                          <option key={sector.id} value={sector.id}>
                            {sector.name} {sector.id === selectedSectorId ? '(Mismo sector)' : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Coordenada Destino</label>
                    <input
                      type="number"
                      value={entityModalForm.coordenada_destino ?? 0}
                      onChange={(e) => setEntityModalForm({ ...entityModalForm, coordenada_destino: Number(e.target.value) })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                      min={0}
                    />
                  </div>
                </>
              )}

              {entityModal.type === 'event' && (
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Evento de Misión</label>
                  <select
                    value={entityModalForm.id_event ?? 0}
                    onChange={(e) => setEntityModalForm({ ...entityModalForm, id_event: Number(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                  >
                    {(missionEventsData || []).map(ev => {
                      const placedInstance = placedMissionEventMap.get(Number(ev.id));
                      const shortText = ev.event_text?.slice(0, 50) || 'Evento';
                      const suffix = placedInstance
                        ? ` (ya colocado${placedInstance.id_sector ? ` en sector ${placedInstance.id_sector}` : ''}${placedInstance.coordinate !== undefined ? ` coord ${placedInstance.coordinate}` : ''})`
                        : '';
                      return (
                        <option
                          key={ev.id}
                          value={ev.id}
                          disabled={!!placedInstance && Number(entityModalForm.id_event) !== Number(ev.id)}
                        >
                          #{ev.id} {shortText}{suffix}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>

            <footer className="p-6 border-t border-slate-800 flex gap-3">
              <button
                type="button"
                onClick={handleEntityModalDelete}
                className="flex-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 font-bold py-3 rounded-xl"
              >
                {objectModalSelectedItem?.source === 'ground' ? 'Eliminar del suelo' : 'Eliminar'}
              </button>
              <button
                type="button"
                onClick={handleEntityModalSave}
                className="flex-[2] bg-game-primary hover:bg-game-primary/80 text-white font-bold py-3 rounded-xl"
                disabled={entityModal.type === 'object' && objectModalSelectedItem?.source === 'ground'}
              >
                {entityModal.type === 'object' && objectModalSelectedItem?.source === 'ground' ? 'Solo lectura' : 'Guardar Cambios'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* MODAL CREAR/EDITAR OBJETO */}
      {showItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-2xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-8 border-b border-slate-800 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                {editingItem ? 'Editar Objeto' : 'Nuevo Objeto'}
              </h2>
              <button
                onClick={() => { setShowItemModal(false); resetForm(); }}
                className="text-slate-500 hover:text-white transition-colors"
              >
                Cerrar
              </button>
            </header>

            <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nombre</label>
                  <input
                    type="text"
                    required
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Descripción</label>
                  <textarea
                    rows="2"
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Tipo</label>
                  <select
                    value={itemForm.item_type}
                    onChange={(e) => setItemForm({ ...itemForm, item_type: parseInt(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors appearance-none"
                  >
                    {ITEM_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nivel Requerido</label>
                  <input
                    type="number"
                    value={itemForm.level_required}
                    onChange={(e) => setItemForm({ ...itemForm, level_required: parseInt(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase flex items-center">
                    Precio (G)
                    <HelpTooltip text="El precio del objeto en Gold (G), la moneda del juego. Es lo que cuesta comprar este objeto en una tienda o mercado. Los jugadores lo pagan con su Gold acumulado." />
                  </label>
                  <input
                    type="number"
                    value={itemForm.value}
                    onChange={(e) => setItemForm({ ...itemForm, value: parseInt(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Peso</label>
                  <input
                    type="number"
                    value={itemForm.weight}
                    onChange={(e) => setItemForm({ ...itemForm, weight: parseInt(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Poder/Daño</label>
                  <input
                    type="number"
                    value={itemForm.power}
                    onChange={(e) => setItemForm({ ...itemForm, power: parseInt(e.target.value) })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Imagen</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={itemForm.image}
                      onChange={(e) => setItemForm({ ...itemForm, image: e.target.value })}
                      placeholder="nombre-archivo.png"
                      className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => { setGalleryTarget('item'); setShowGallery(true); }}
                      className="px-4 py-2 bg-slate-700 hover:bg-game-primary text-white rounded-xl transition-colors text-sm font-bold flex items-center gap-2"
                    >
                      <Image size={16} /> Galería
                    </button>
                  </div>
                  {itemForm.image && itemForm.image !== 'default.png' && (
                    <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${itemForm.image}`} alt="preview" className="h-20 rounded-xl object-cover border border-slate-700" />
                  )}
                </div>
              </div>

              <footer className="pt-6 border-t border-slate-800 flex gap-4">
                <button
                  type="button"
                  onClick={() => { setShowItemModal(false); resetForm(); }}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 rounded-2xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="flex-[2] bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-game-primary/20"
                >
                  {editingItem ? 'Guardar Cambios' : 'Crear Objeto'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CREAR ENEMIGO */}
      {showEnemyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-2xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-8 border-b border-slate-800 flex justify-between items-center text-white">
              <h2 className="text-2xl font-bold uppercase tracking-tight">{editingEnemy ? 'Editar Bot Enemigo' : 'Nuevo Bot Enemigo'}</h2>
              <button onClick={() => { setShowEnemyModal(false); resetEnemyForm(); }} className="text-slate-500 hover:text-white transition-colors">Cerrar</button>
            </header>
            <form onSubmit={(e) => { e.preventDefault(); editingEnemy ? updateEnemyMutation.mutate({ id: editingEnemy.id, form: enemyForm }) : createEnemyMutation.mutate(enemyForm); }} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nombre del Enemigo</label>
                  <input type="text" required value={enemyForm.name} onChange={(e) => setEnemyForm({ ...enemyForm, name: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nivel</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={enemyForm.level}
                      onChange={(e) => setEnemyForm({ ...enemyForm, level: parseInt(e.target.value) || 1 })}
                      className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setEnemyForm((current) => ({
                        ...current,
                        ...buildEnemySuggestionFromLevel(current.level)
                      }))}
                      className="px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors text-sm font-bold"
                    >
                      Sugerir stats
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Imagen</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={enemyForm.image}
                      readOnly
                      placeholder="enemy1.png"
                      className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white/80 focus:outline-none cursor-default"
                    />
                    <button
                      type="button"
                      onClick={() => { setGalleryTarget('enemy'); setShowGallery(true); }}
                      className="px-4 py-2 bg-slate-700 hover:bg-game-primary text-white rounded-xl transition-colors text-sm font-bold flex items-center gap-2"
                    >
                      <Image size={16} /> Galería
                    </button>
                    <button
                      type="button"
                      onClick={() => setEnemyForm({ ...enemyForm, image: 'enemy1.png' })}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors text-sm font-bold"
                    >
                      Limpiar
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    {enemyForm.image && enemyForm.image !== 'enemy1.png' ? (
                      <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${enemyForm.image}`} alt="preview" className="h-20 w-20 rounded-xl object-cover border border-slate-700" />
                    ) : (
                      <div className="h-20 w-20 rounded-xl border border-dashed border-slate-700 bg-slate-900/40 flex items-center justify-center text-slate-500 text-xs">
                        Sin imagen
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      Selecciona una imagen desde la galeria para usarla como retrato del enemigo.
                    </p>
                  </div>
                </div>
                <div className="col-span-2 grid grid-cols-3 gap-3 p-4 bg-slate-800/20 rounded-2xl border border-slate-800">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Vida Cabeza</label>
                    <input type="number" value={enemyForm.v_head} onChange={(e) => setEnemyForm({ ...enemyForm, v_head: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Vida Torso</label>
                    <input type="number" value={enemyForm.v_torso} onChange={(e) => setEnemyForm({ ...enemyForm, v_torso: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div className="space-y-1 text-center">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Total HP</label>
                    <div className="text-lg font-bold text-game-primary">{enemyForm.v_head + enemyForm.v_torso + enemyForm.v_arm_l + enemyForm.v_arm_r + enemyForm.v_leg_l + enemyForm.v_leg_r}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Recompensa EXP</label>
                  <input type="number" value={enemyForm.experience_reward} onChange={(e) => setEnemyForm({ ...enemyForm, experience_reward: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Recompensa Oro</label>
                  <input type="number" value={enemyForm.money_reward} onChange={(e) => setEnemyForm({ ...enemyForm, money_reward: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                </div>
              </div>
              <footer className="pt-6 border-t border-slate-800 flex gap-4">
                <button type="button" onClick={() => { setShowEnemyModal(false); resetEnemyForm(); }} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 rounded-2xl transition-all">Cancelar</button>
                <button type="submit" disabled={createEnemyMutation.isLoading || updateEnemyMutation.isLoading} className="flex-[2] bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-4 rounded-2xl shadow-lg">{editingEnemy ? 'Guardar Bot' : 'Crear Enemigo'}</button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CREAR CIUDAD */}
      {showCityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-8 border-b border-slate-800 flex justify-between items-center text-white">
              <h2 className="text-2xl font-bold uppercase tracking-tight">{editingCity ? 'Editar Ciudad' : 'Nueva Ciudad'}</h2>
              <button onClick={() => { setShowCityModal(false); resetCityForm(); }} className="text-slate-500 hover:text-white transition-colors">Cerrar</button>
            </header>
            <form onSubmit={handleCitySubmit} className="p-8 space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nombre de la Ciudad</label>
                  <input type="text" required value={cityForm.name} onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Tipo</label>
                    <select value={cityForm.city_type} onChange={(e) => setCityForm({ ...cityForm, city_type: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                      <option value={0}>Campamento</option>
                      <option value={1}>Pueblo</option>
                      <option value={2}>Ciudad</option>
                      <option value={3}>Capital</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Clan Dominante</label>
                    <select
                      value={cityForm.clan ?? 0}
                      onChange={(e) => setCityForm({ ...cityForm, clan: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                    >
                      <option value={0}>Neutral</option>
                      {safeClansData.map((clan) => (
                        <option key={clan.id} value={clan.id}>
                          {clan.name}
                        </option>
                      ))}
                    </select>
                    {isLoadingClans && <p className="text-xs text-slate-500">Cargando clanes...</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Imagen</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cityForm.image}
                      onChange={(e) => setCityForm({ ...cityForm, image: e.target.value })}
                      placeholder="city.png"
                      className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => { setGalleryTarget('city'); setShowGallery(true); }}
                      className="px-4 py-2 bg-slate-700 hover:bg-game-primary text-white rounded-xl transition-colors text-sm font-bold flex items-center gap-2"
                    >
                      <Image size={16} /> Galería
                    </button>
                  </div>
                  {cityForm.image && cityForm.image !== 'city1.png' && (
                    <img src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${cityForm.image}`} alt="preview" className="h-20 rounded-xl object-cover border border-slate-700" />
                  )}
                </div>
              </div>
              <footer className="pt-6 border-t border-slate-800 flex gap-4">
                <button type="button" onClick={() => { setShowCityModal(false); resetCityForm(); }} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 rounded-2xl transition-all">Cancelar</button>
                <button type="submit" disabled={createCityMutation.isLoading || updateCityMutation.isLoading} className="flex-[2] bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-4 rounded-2xl shadow-lg">{editingCity ? 'Guardar Ciudad' : 'Establecer Ciudad'}</button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crear Sector */}
      {showSectorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-[#11132d] w-full max-w-xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <header className="p-8 border-b border-slate-800 flex justify-between items-center text-white">
              <h2 className="text-2xl font-bold uppercase tracking-tight">{editingSector ? 'Editar Sector' : 'Nuevo Sector'}</h2>
              <button onClick={() => { setShowSectorModal(false); resetSectorForm(); }} className="text-slate-500 hover:text-white transition-colors">Cerrar</button>
            </header>
            <form onSubmit={handleSectorSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Nombre del Sector</label>
                  <input type="text" required value={sectorForm.name} onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" placeholder="Sector 1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Ancho</label>
                    <input type="number" required value={sectorForm.width} onChange={(e) => setSectorForm({ ...sectorForm, width: e.target.value })} min={5} max={50} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Alto</label>
                    <input type="number" required value={sectorForm.height} onChange={(e) => setSectorForm({ ...sectorForm, height: e.target.value })} min={5} max={50} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Tipo</label>
                    <select value={sectorForm.sector_type} onChange={(e) => setSectorForm({ ...sectorForm, sector_type: parseInt(e.target.value) })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                      <option value={0}>Superficie</option>
                      <option value={1}>Subterráneo</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-400 uppercase">Temperatura</label>
                    <input type="number" value={sectorForm.temperature} onChange={(e) => setSectorForm({ ...sectorForm, temperature: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Clima</label>
                  <select value={sectorForm.climate} onChange={(e) => setSectorForm({ ...sectorForm, climate: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white">
                    <option value="templado">Templado</option>
                    <option value="frio">Frío</option>
                    <option value="calido">Cálido</option>
                    <option value="desertico">Desértico</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Tileset</label>
                  <select
                    value={sectorForm.tileset_id}
                    onChange={(e) => setSectorForm({ ...sectorForm, tileset_id: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white"
                  >
                    <option value="overworld_v1">Overworld</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 uppercase">Descripción</label>
                  <textarea value={sectorForm.description} onChange={(e) => setSectorForm({ ...sectorForm, description: e.target.value })} rows={3} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-game-primary transition-colors" placeholder="Descripción del sector..." />
                </div>
              </div>
              <footer className="pt-6 border-t border-slate-800 flex gap-4">
                <button type="button" onClick={() => { setShowSectorModal(false); resetSectorForm(); }} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 rounded-2xl transition-all">Cancelar</button>
                <button type="submit" disabled={createSectorMutation.isLoading || updateSectorMutation.isLoading} className="flex-[2] bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold py-4 rounded-2xl shadow-lg">{editingSector ? 'Guardar Cambios' : 'Crear Sector'}</button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Galería Global de Imágenes */}
      <GalleryModal
        isOpen={showGallery}
      onClose={() => setShowGallery(false)}
      onSelect={(name, url) => {
        if (galleryTarget === 'item') setItemForm(f => ({ ...f, image: name }));
        else if (galleryTarget === 'enemy') setEnemyForm(f => ({ ...f, image: name }));
        else if (galleryTarget === 'city') setCityForm(f => ({ ...f, image: name }));
        else if (galleryTarget === 'mission-event') setMissionEventForm(f => ({ ...f, image: name }));
        else if (galleryTarget === 'mission-event-edit') setMissionEventEditForm(f => ({ ...f, image: name }));
        else if (galleryTarget === 'rpg-tileset') rpgWorldManagerRef.current?.setTilesetImage?.(name);
        else if (galleryTarget === 'rpg-city-config') rpgWorldManagerRef.current?.setCityBackgroundImage?.(name);
        else if (galleryTarget === 'rpg-npc-template') rpgWorldManagerRef.current?.setNpcSpriteImage?.(name);
        else if (galleryTarget === 'rpg-player-appearance') rpgWorldManagerRef.current?.setPlayerSpriteImage?.(name);
        setGalleryTarget(null);
      }}
      />

      <MapTestModal
        isOpen={showMapTestModal}
        onClose={() => setShowMapTestModal(false)}
        sector={sectorDetail?.sector}
        tiles={localTiles}
        tilesetConfig={tilesetOverworldConfig}
        portals={localPortals}
        enemies={enemyInstances}
        objects={objectInstances}
        cities={sectorCities}
        missionEvents={missionEventsData}
      />
    </div>
  );
}



